import { __decorate, __metadata, __param } from "tslib";
import { Container, inject, injectable, multiInject, named, optional, tagged } from "inversify";
import { openDB } from "idb";
import "reflect-metadata";
import { addSeconds, isBefore } from "date-fns";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import * as signalR from "@microsoft/signalr";
import i18next from "i18next";
import { init } from "@elastic/apm-rum";
import axios from "axios";
function extractUrlParts(url) {
	const a = document.createElement("a");
	a.href = url;
	return {
		url: a.href,
		host: a.host,
		hostname: a.hostname,
		port: a.port,
		pathname: a.pathname,
		protocol: a.protocol,
		hash: a.hash,
		search: a.search,
		origin: extractUrlOrigin(url)
	};
}
function extractUrlOrigin(url) {
	if (url == null || url.length == 0) return null;
	const pathArray = url.split("/");
	const protocol = pathArray[0];
	const host = pathArray[2];
	return protocol + "//" + host;
}
function checkUrlAbsolute(url) {
	return (/* @__PURE__ */ new RegExp("^(?:[a-z]+:)?//", "i")).test(url);
}
function parseQueryStringValue(name, url = null) {
	if (url == null) url = window.location.href;
	name = name.replace(/[[\]]/g, "\\$&");
	const results = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)").exec(url);
	if (!results) return null;
	if (!results[2]) return "";
	return decodeURIComponent(results[2].replace(/\+/g, " "));
}
function resolveUrlFromOrigin(url) {
	return checkUrlAbsolute(url) ? url : document.location.origin + url;
}
function openUrl(url, target = null) {
	const a = document.createElement("a");
	a.href = url;
	if (target != null) a.target = target;
	document.body.appendChild(a);
	a.click();
	a.parentNode.removeChild(a);
}
function checkLocalhost() {
	return window.location.hostname === "localhost";
}
function checkSsl() {
	return window.location.protocol === "https:";
}
function concatUrl(...urlSegments) {
	let result = null;
	if (urlSegments && urlSegments.length) {
		result = "";
		urlSegments.forEach((urlSegment) => {
			if (urlSegment && urlSegment.length) {
				if (result.length && !result.endsWith("/") && !urlSegment.startsWith("/")) result += "/";
				else if (result.endsWith("/") && urlSegment.startsWith("/")) result = result.substring(0, result.length - 1);
				result += urlSegment;
			}
		});
	}
	return result;
}
function _checkCompatibleOrigin(url, compatibleOrigins) {
	if (compatibleOrigins?.length) {
		const origin = new URL(url).origin.toLowerCase();
		return compatibleOrigins.some((compatibleOrigin) => {
			let value = compatibleOrigin.value.toLowerCase();
			if (value.endsWith("/")) value = value.substring(0, value.length - 1);
			if (compatibleOrigin.strategy === "exact" && origin === value) return true;
			else if (compatibleOrigin.strategy === "startsWith" && origin.startsWith(value)) return true;
		});
	}
	return false;
}
function clearQueryStringParams(uri, condition) {
	const url = new URL(uri);
	const keysToDelete = [];
	url.searchParams.forEach((value, key) => {
		if (condition(key)) keysToDelete.push(key);
	});
	keysToDelete.forEach((key) => url.searchParams.delete(key));
	return url.toString();
}
const BAAS_TYPES = {
	AxiosInstance: Symbol("AxiosInstance"),
	BaasAuthStrategy: Symbol("BaasAuthStrategy"),
	BaasContextProvider: Symbol("BaasContextProvider"),
	BaasLogOffHook: Symbol("BaasLogOffHook"),
	BaasLoggingApiClient: Symbol("BaasLoggingApiClient"),
	BaasMeldingApiClient: Symbol("BaasMeldingApiClient"),
	BaasSecurityApiClient: Symbol("BaasSecurityApiClient"),
	BaasAuditApiClient: Symbol("BaasAuditApiClient"),
	BaasConfigurationApiClient: Symbol("BaasConfigurationApiClient"),
	BaasMeetpuntApiClient: Symbol("BaasMeetpuntApiClient"),
	BaasCommonApiClient: Symbol("BaasCommonApiClient"),
	BarGvkRemoteStoreProvider: Symbol("BarGvkRemoteStoreProvider"),
	BarErrorDelegate: Symbol("BarErrorDelegate"),
	BaasAuthFailedStrategy: Symbol("BaasAuthFailedStrategy"),
	BaasTelemetry: Symbol("BaasTelemetry"),
	_BaasLvOicdAuthService: Symbol("BaasLvOicdAuthService")
};
const BAAS_AXIOS_INSTANCE_TYPES = {
	Baas: Symbol("Baas"),
	_BaasSilent: Symbol("BaasSilent"),
	Plain: Symbol("Plain")
};
function readCookie(name) {
	const nameEQ = name + "=";
	const ca = document.cookie.split(";");
	for (const c of ca) {
		let cookie = c;
		while (cookie.charAt(0) === " ") cookie = cookie.substring(1, cookie.length);
		if (cookie.indexOf(nameEQ) === 0) return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
	}
	return null;
}
function deleteAllCookies() {
	const cookies = document.cookie.split(";");
	for (const cookie of cookies) {
		const eqPos = cookie.indexOf("=");
		deleteCookie(eqPos > -1 ? cookie.substr(0, eqPos) : cookie);
	}
}
function deleteCookie(name) {
	document.cookie = name + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
}
function checkDateObject(value) {
	return Object.prototype.toString.call(value) === "[object Date]";
}
function convertToISOString(value) {
	if (!value) return value;
	return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), value.getHours(), value.getMinutes(), value.getSeconds(), value.getMilliseconds())).toISOString();
}
function checkHasParentDomElement(element, parentSelector) {
	while (element) {
		if (element.matches(parentSelector)) return true;
		element = element.parentElement;
	}
	return false;
}
function asciiEncode(input) {
	return input.replace(/[\u0080-\uFFFF]/g, (char) => {
		return `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`;
	});
}
function encodeHeaders(headers) {
	const encoded = {};
	for (const key in headers) {
		let value = headers[key];
		value = value.replace(/'/g, "\\u0027");
		value = asciiEncode(value);
		encoded[key] = value;
	}
	return encoded;
}
function deleteAllIndexedDb() {
	return new Promise((resolve, reject) => {
		window.indexedDB.databases().then((r) => {
			for (const db of r) {
				const req = window.indexedDB.deleteDatabase(db.name);
				req.onerror = (event) => {
					console.error(`[IndexedDB] Error deleting database '${db.name}':`, event.target?.error);
				};
			}
		}).then(() => {
			resolve();
		}).catch((error) => {
			console.error("[IndexedDB] Error enumerating databases for deleteAllIndexedDb:", error);
			reject(error);
		});
	});
}
function deleteIndexedDb(name) {
	const request = window.indexedDB.deleteDatabase(name);
	request.onerror = (event) => {
		console.error(`[IndexedDB] Error deleting database '${name}':`, event.target?.error);
	};
}
function getIndexedDbInfo(name) {
	return new Promise((resolve, reject) => {
		const request = window.indexedDB.open(name);
		request.onsuccess = (event) => {
			const db = event.target.result;
			db.close();
			resolve({
				version: event.target.result.version,
				objectStoreNames: [...db.objectStoreNames]
			});
		};
		request.onerror = (event) => {
			console.error(`[IndexedDB] Error opening database '${name}' in getIndexedDbInfo:`, event.target?.error);
			reject(event.target?.error);
		};
	});
}
function _deleteIndexedDbObjectStores(name, version, storeNames) {
	return new Promise((resolve, reject) => {
		const request = indexedDB.open(name, version);
		request.onupgradeneeded = (event) => {
			const db = event.target.result;
			storeNames.forEach((storeName) => {
				db.deleteObjectStore(storeName);
			});
			db.close();
			resolve();
		};
		request.onsuccess = (event) => {
			event.target.result.close();
			resolve();
		};
		request.onerror = (event) => {
			console.error(`[IndexedDB] Error in _deleteIndexedDbObjectStores (db='${name}', version=${version}, stores=[${storeNames.join(", ")}]):`, event.target?.error);
			reject(event.target?.error);
		};
	});
}
let cachedResult;
function isCrossOriginIframe() {
	try {
		return window.self !== window.top && window.location.origin !== window.parent.location.origin;
	} catch {
		return true;
	}
}
function isStorageAccessApiSupported() {
	return "requestStorageAccess" in document && typeof document.requestStorageAccess === "function";
}
function getStorageAccess() {
	return cachedResult ? { ...cachedResult } : void 0;
}
async function requestStorageAccess() {
	if (!cachedResult) cachedResult = {
		hasAccess: true,
		isCrossOriginIframe: isCrossOriginIframe(),
		isApiSupported: isStorageAccessApiSupported()
	};
	if (!cachedResult.isCrossOriginIframe) return;
	if (!cachedResult.isApiSupported) {
		console.info("[BAR] Storage Access API not supported. Assuming storage access is available.");
		return;
	}
	try {
		if (await document.hasStorageAccess()) {
			cachedResult.hasAccess = true;
			return;
		}
		await document.requestStorageAccess({
			indexedDB: true,
			localStorage: true,
			sessionStorage: true
		});
		cachedResult.hasAccess = true;
		console.info("[BAR] Storage access granted for cross-origin iframe.");
	} catch (error) {
		cachedResult.hasAccess = false;
		console.warn("[BAR] Storage access denied for cross-origin iframe. IndexedDB may not work correctly.", error);
	}
}
function generateUuid() {
	let d = Date.now();
	if (window.performance && typeof window.performance.now === "function") d += performance.now();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
		const r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		return (c == "x" ? r : r & 3 | 8).toString(16);
	});
}
function resolveValue(value) {
	if (typeof value === "function") return value();
	else return value;
}
function ensureGlobal(nestedObject) {
	let result = null;
	if (nestedObject != null && nestedObject.length > 0) {
		let global = window;
		nestedObject.split(".").forEach((piece) => {
			if (global[piece] == null) global[piece] = {};
			global = global[piece];
		});
		result = global;
	}
	return result;
}
function getParamNames(func) {
	let str = func.toString();
	str = str.replace(/\/\*[\s\S]*?\*\//g, "").replace(/\/\/(.)*/g, "").replace(/{[\s\S]*}/, "").replace(/=>/g, "").trim();
	const start = str.indexOf("(") + 1;
	const end = str.length - 1;
	const result = str.substring(start, end).split(", ");
	const params = [];
	result.forEach((element) => {
		element = element.replace(/=[\s\S]*/g, "").trim();
		if (element.length > 0) params.push(element);
	});
	return params;
}
function getStack() {
	try {
		throw new Error();
	} catch (e) {
		return e.stack.replace("Error\n", "").trim();
	}
}
function versionCompare(v1, v2, options = void 0) {
	const lexicographical = options && options.lexicographical;
	const zeroExtend = options && options.zeroExtend;
	let v1parts = v1.split(".");
	let v2parts = v2.split(".");
	function isValidPart(x) {
		return (lexicographical ? /^\d+[A-Za-z]*$/ : /^\d+$/).test(x);
	}
	if (!v1parts.every(isValidPart) || !v2parts.every(isValidPart)) return NaN;
	if (zeroExtend) {
		while (v1parts.length < v2parts.length) v1parts.push("0");
		while (v2parts.length < v1parts.length) v2parts.push("0");
	}
	if (!lexicographical) {
		v1parts = v1parts.map(Number);
		v2parts = v2parts.map(Number);
	}
	for (let i = 0; i < v1parts.length; ++i) {
		if (v2parts.length == i) return 1;
		if (v1parts[i] == v2parts[i]) continue;
		else if (v1parts[i] > v2parts[i]) return 1;
		else return -1;
	}
	if (v1parts.length != v2parts.length) return -1;
	return 0;
}
function generateRandomNumber(min, max) {
	const difference = max - min;
	let rand = Math.random();
	rand = Math.floor(rand * difference);
	return rand + min;
}
var BaasAuthOptions = class {
	constructor() {
		this.enabled = true;
		this.validation = true;
		this.withActingScopeHeaders = false;
		this.syncBasiscontextAfterRequestCompletion = true;
	}
};
var BaasBackendType;
(function(BaasBackendType) {
	BaasBackendType["Baas"] = "Baas";
	BaasBackendType["Bar"] = "Bar";
	BaasBackendType["Custom"] = "Custom";
})(BaasBackendType || (BaasBackendType = {}));
let BaasBootstrapOptions = class BaasBootstrapOptions {
	constructor() {
		this.auth = new BaasAuthOptions();
		this.backendType = BaasBackendType.Baas;
		this.i18n = {};
	}
};
BaasBootstrapOptions = __decorate([injectable()], BaasBootstrapOptions);
var BaasBootstrapOutcome;
(function(BaasBootstrapOutcome) {
	BaasBootstrapOutcome["Initialized"] = "Initialized";
	BaasBootstrapOutcome["AuthFailed"] = "AuthFailed";
	BaasBootstrapOutcome["Authenticating"] = "Authenticating";
	BaasBootstrapOutcome["Halted"] = "Halted";
})(BaasBootstrapOutcome || (BaasBootstrapOutcome = {}));
var BarLogger = class {
	#enabled;
	#format;
	#checkEnabled() {
		if (this.#enabled == null) {
			if (this.options?.category) {
				this.#enabled = false;
				const item = window.localStorage.getItem(`lvflanders.bar.shared.log.enabled.${this.options.category}`);
				if (item != null && item.length > 0) this.#enabled = JSON.parse(item);
			} else this.#enabled = true;
		}
		return this.#enabled;
	}
	#getFormat() {
		if (this.#format == null) {
			const item = window.localStorage.getItem("lvflanders.bar.shared.log.format");
			if (item != null && item.length > 0) switch (item.toLowerCase()) {
				case "json":
					this.#format = "json";
					break;
				default: this.#format = "object";
			}
		}
		return this.#format;
	}
	constructor(options) {
		this.options = options;
	}
	log(message) {
		if (this.#checkEnabled()) {
			this.#enrich(message);
			this.#output(console.log, message);
		}
	}
	error(message) {
		this.#enrich(message);
		this.#output(console.error, message);
	}
	#enrich(message) {
		if (!message.category) message.category = this.options?.category;
		if (!message.context) message.context = this.options?.context;
		if (!message.stack) message.stack = getStack();
		if (this.options?.correlation) {
			if (message.correlation) message.correlation.parent = this.options.correlation;
			else message.correlation = this.options.correlation;
		}
	}
	#output(fn, message) {
		switch (this.#getFormat()) {
			case "json":
				fn(JSON.stringify(message));
				break;
			default: fn(message);
		}
	}
};
let BaasAppState = class BaasAppState {};
BaasAppState = __decorate([injectable()], BaasAppState);
var BarStore = class {
	#logger;
	#dbName;
	#storeName;
	constructor(name, storeName) {
		this.#dbName = name;
		this.#storeName = storeName;
		this.#logger = new BarLogger({
			category: "cacheStorage",
			context: {
				name,
				storeName,
				type: "BarStore (IndexedDB)"
			}
		});
	}
	async getItem(key) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin getItem with key '${key}'`,
			correlation
		});
		let result = void 0;
		const db = await this.#getDB(false);
		if (db) try {
			if (db.objectStoreNames.contains(this.#storeName)) {
				const tx = db.transaction(this.#storeName, "readonly");
				result = await tx.objectStore(this.#storeName).get(key);
				await tx.done;
			}
		} catch (error) {
			this.#logger.error({
				message: `Error in getItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			db.close();
		}
		this.#logger.log({
			message: `End getItem with key '${key}'`,
			correlation
		});
		return result;
	}
	async setItem(key, value) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin setItem with key '${key}'`,
			correlation
		});
		const db = await this.#getDB(true);
		try {
			const tx = db.transaction(this.#storeName, "readwrite");
			await tx.objectStore(this.#storeName).put(value, key);
			await tx.done;
		} catch (error) {
			this.#logger.error({
				message: `Error in setItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			db.close();
		}
		this.#logger.log({
			message: `End setItem with key '${key}'`,
			correlation
		});
	}
	async removeItem(key) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin removeItem with key '${key}'`,
			correlation
		});
		const db = await this.#getDB(false);
		if (db) try {
			if (db.objectStoreNames.contains(this.#storeName)) {
				const tx = db.transaction(this.#storeName, "readwrite");
				await tx.objectStore(this.#storeName).delete(key);
				await tx.done;
			}
		} catch (error) {
			this.#logger.error({
				message: `Error in removeItem (db='${this.#dbName}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			db.close();
		}
		this.#logger.log({
			message: `End removeItem with key '${key}'`,
			correlation
		});
	}
	async clear() {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: "Begin clearObjectStore",
			correlation
		});
		const db = await this.#getDB(false);
		if (db) try {
			if (db.objectStoreNames.contains(this.#storeName)) {
				const tx = db.transaction(this.#storeName, "readwrite");
				await tx.objectStore(this.#storeName).clear();
				await tx.done;
			}
		} catch (error) {
			this.#logger.error({
				message: `Error in clear (db='${this.#dbName}', store='${this.#storeName}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			db.close();
		}
		this.#logger.log({
			message: "End clearObjectStore",
			correlation
		});
	}
	async drop() {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: "Begin dropObjectStore",
			correlation
		});
		let db = await this.#getDB(false);
		if (db) try {
			if (db.objectStoreNames.contains(this.#storeName)) {
				db.close();
				db = await openDB(this.#dbName, db.version + 1, { upgrade: (db) => {
					if (db.objectStoreNames.contains(this.#storeName)) db.deleteObjectStore(this.#storeName);
				} });
			}
		} catch (error) {
			this.#logger.error({
				message: `Error in drop (db='${this.#dbName}', store='${this.#storeName}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			db.close();
		}
		this.#logger.log({
			message: "End dropObjectStore",
			correlation
		});
	}
	async #getDB(allowCreate) {
		const dbExists = (await indexedDB.databases()).some((db) => db.name === this.#dbName);
		if (!dbExists && !allowCreate) return void 0;
		let db;
		try {
			const options = dbExists ? void 0 : { upgrade: (upgradeDb) => {
				if (!upgradeDb.objectStoreNames.contains(this.#storeName)) upgradeDb.createObjectStore(this.#storeName);
			} };
			db = await openDB(this.#dbName, void 0, options);
			if (allowCreate && dbExists && !db.objectStoreNames.contains(this.#storeName)) {
				db.close();
				db = await openDB(this.#dbName, db.version + 1, { upgrade: (upgradeDb) => {
					if (!upgradeDb.objectStoreNames.contains(this.#storeName)) upgradeDb.createObjectStore(this.#storeName);
				} });
			}
		} catch (error) {
			this.#logger.error({ message: `Error opening database (db='${this.#dbName}', store='${this.#storeName}'): ${error}` });
			throw error;
		}
		return db;
	}
};
function _getDefaultCacheEntryOptions() {
	return { session: true };
}
var BarCacheStore = class {
	#name;
	#storeName;
	#store;
	#logger;
	#getContextData;
	constructor(name, storeName, getContextData = () => {
		return {};
	}, correlation = void 0) {
		this.#name = name;
		this.#storeName = storeName;
		this.#getContextData = getContextData;
		this.#logger = new BarLogger({
			category: "cacheStorage",
			context: {
				type: "BarCacheStore",
				name,
				storeName
			},
			correlation
		});
		this.#store = new BarStore(name, storeName);
	}
	async getOrSetItem(key, callback, options = _getDefaultCacheEntryOptions()) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin getOrSetItem with key '${key}'`,
			correlation
		});
		try {
			const value = await this.#getItemInternal(key);
			if (value != null) return value;
			const data = await Promise.resolve(callback());
			await this.#setItemInternal(key, data, options);
			return data;
		} catch (error) {
			this.#logger.error({
				message: `Error in getOrSetItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: `End getOrSetItem with key '${key}'`,
				correlation
			});
		}
	}
	async getItem(key) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin getItem with key '${key}'`,
			correlation
		});
		try {
			return await this.#getItemInternal(key);
		} catch (error) {
			this.#logger.error({
				message: `Error in getItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: `End getItem with key '${key}'`,
				correlation
			});
		}
	}
	async setItem(key, value, options = _getDefaultCacheEntryOptions()) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin setItem with key '${key}'`,
			correlation
		});
		try {
			await this.#setItemInternal(key, value, options);
		} catch (error) {
			this.#logger.error({
				message: `Error in setItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: `End setItem with key '${key}'`,
				correlation
			});
		}
	}
	async removeItem(key) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin removeItem with key '${key}'`,
			correlation
		});
		try {
			await this.#store.removeItem(key);
		} catch (error) {
			this.#logger.error({
				message: `Error in removeItem (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: `End removeItem with key '${key}'`,
				correlation
			});
		}
	}
	async clear() {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: "Begin clear",
			correlation
		});
		try {
			await this.#store.clear();
		} catch (error) {
			this.#logger.error({
				message: `Error in clear (db='${this.#name}', store='${this.#storeName}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: "End clear",
				correlation
			});
		}
	}
	async drop() {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: "Begin drop",
			correlation
		});
		try {
			await this.#store.drop();
		} catch (error) {
			this.#logger.error({
				message: `Error in drop (db='${this.#name}', store='${this.#storeName}'): ${error}`,
				correlation
			});
			throw error;
		} finally {
			this.#logger.log({
				message: "End drop",
				correlation
			});
		}
	}
	async invalidate(clearSession = false) {
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Begin invalidate with clearSession = ${clearSession}`,
			correlation
		});
		try {
			let keys;
			let db = await openDB(this.#name);
			try {
				const tx = db.transaction(this.#storeName, "readwrite");
				const store = tx.objectStore(this.#storeName);
				keys = await store.getAllKeys();
				for (const key of keys) {
					const entry = await tx.objectStore(this.#storeName).get(key);
					if (entry && this.#checkExpired(entry) || clearSession && entry.options != null && entry.options.session) await tx.objectStore(this.#storeName).delete(key);
				}
				keys = await store.getAllKeys();
				await tx.done;
			} finally {
				db.close();
			}
			if (!keys.length) {
				db = await openDB(this.#name, db.version + 1, { upgrade: (db) => {
					if (db.objectStoreNames.contains(this.#storeName)) db.deleteObjectStore(this.#storeName);
				} });
				db.close();
			}
		} catch (error) {
			this.#logger.error({
				message: `Error in invalidate (db='${this.#name}', store='${this.#storeName}', clearSession=${clearSession}): ${error}`,
				correlation
			});
			throw error;
		}
		this.#logger.log({
			message: `End invalidate with clearSession = ${clearSession}`,
			correlation
		});
	}
	#checkExpired(entry) {
		const contextData = this.#getContextData() || {};
		return entry.expiration != null && isBefore(entry.expiration, /* @__PURE__ */ new Date()) || entry.options?.session && contextData.sessionId && entry.sessionId != contextData.sessionId;
	}
	async #getItemInternal(key) {
		let value = null;
		const db = await openDB(this.#name);
		try {
			if (db.objectStoreNames.contains(this.#storeName)) {
				const tx = db.transaction(this.#storeName, "readwrite");
				const store = tx.objectStore(this.#storeName);
				const entry = await store.get(key);
				if (entry) {
					if (this.#checkExpired(entry)) await store.delete(key);
					else {
						entry.lastAccess = /* @__PURE__ */ new Date();
						await store.put(entry, key);
						value = entry.value;
					}
				}
				await tx.done;
			}
		} catch (error) {
			this.#logger.error({ message: `Error in getItemInternal (db='${this.#name}', store='${this.#storeName}', key='${key}'): ${error}` });
			throw error;
		} finally {
			db.close();
		}
		return value;
	}
	async #setItemInternal(key, value, options) {
		const timestamp = /* @__PURE__ */ new Date();
		const contextData = this.#getContextData() || {};
		const entry = {
			value,
			timestamp,
			expiration: options != null && options.ttl != null ? addSeconds(timestamp, options.ttl) : null,
			sessionId: contextData.sessionId,
			options
		};
		await this.#store.setItem(key, entry);
	}
};
var BaasNullCacheStore = class extends BarCacheStore {
	constructor() {
		super("_null", "_null");
	}
	async getOrSetItem(_key, callback, _options) {
		return Promise.resolve(callback());
	}
	async getItem() {}
	async setItem() {}
	async removeItem() {}
	async clear() {}
	async drop() {}
	async invalidate() {}
};
var BarCacheStoreType;
(function(BarCacheStoreType) {
	BarCacheStoreType["Application"] = "Application";
	BarCacheStoreType["User"] = "User";
	BarCacheStoreType["Global"] = "Global";
	BarCacheStoreType["ApplicationUser"] = "ApplicationUser";
})(BarCacheStoreType || (BarCacheStoreType = {}));
const DB_NAME_PREFIX = "domain.lv.cache.";
const DB_NAME_APP = `${DB_NAME_PREFIX}app`;
const DB_NAME_USER = `${DB_NAME_PREFIX}user`;
const DB_NAME_APP_USER = `${DB_NAME_PREFIX}appUser`;
const DB_NAME_GLOBAL = `${DB_NAME_PREFIX}global`;
const DB_NAME_GVK = `${DB_NAME_PREFIX}gvk`;
let BarCacheManager = class BarCacheManager {
	#logger = new BarLogger({
		category: "cacheStorage",
		context: "BarCacheManager"
	});
	#appState;
	#options;
	constructor(appState, options) {
		this.#appState = appState;
		this.#options = options;
	}
	getStore(options) {
		let name = null;
		let storeName = null;
		switch (options.storeType) {
			case BarCacheStoreType.Application:
				name = DB_NAME_APP;
				storeName = options.appCode;
				break;
			case BarCacheStoreType.User:
				name = DB_NAME_USER;
				storeName = options.userId;
				break;
			case BarCacheStoreType.ApplicationUser:
				name = DB_NAME_APP_USER;
				storeName = `${options.appCode}_${options.userId}`;
				break;
			case BarCacheStoreType.Global:
				name = DB_NAME_GLOBAL;
				storeName = "main";
		}
		if (options.store != null) storeName += `_${options.store}`;
		return this.#getStore(name, storeName);
	}
	async getStores(options) {
		if (!options) throw new Error("Options are required.");
		if (!options.storeType) throw new Error("Store type is required.");
		if (this.#options.cacheStorage?.type === "none") return [];
		let dbName;
		let filterFn;
		switch (options.storeType) {
			case BarCacheStoreType.Application:
				dbName = DB_NAME_APP;
				filterFn = (storeName) => {
					const { appCode, store } = options;
					if (appCode === "*") return true;
					if (appCode && store === "*") return storeName.startsWith(`${appCode}_`);
					if (appCode && store) return storeName === `${appCode}_${store}`;
					if (appCode && !store) return storeName === appCode;
					return false;
				};
				break;
			case BarCacheStoreType.User:
				dbName = DB_NAME_USER;
				filterFn = (storeName) => {
					const { userId, store } = options;
					if (userId === "*") return true;
					if (userId && store === "*") return storeName.startsWith(`${userId}_`);
					if (userId && store) return storeName === `${userId}_${store}`;
					if (userId && !store) return storeName === userId;
					return false;
				};
				break;
			case BarCacheStoreType.ApplicationUser:
				dbName = DB_NAME_APP_USER;
				filterFn = (storeName) => {
					const { appCode, userId, store } = options;
					if (appCode === "*" && userId === "*") return true;
					if (appCode === "*" && userId && userId !== "*") {
						const suffix = `_${userId}`;
						if (store === "*") return storeName.endsWith(`${suffix}_${store}`);
						if (store != null) return storeName.endsWith(`${suffix}_${store}`);
						return storeName.endsWith(suffix) && !storeName.endsWith(`${suffix}_`);
					}
					if (userId === "*" && appCode && appCode !== "*") {
						const prefix = `${appCode}_`;
						if (store === "*") return storeName.startsWith(`${prefix}`);
						if (store != null) return storeName.startsWith(`${prefix}`) && storeName.endsWith(`_${store}`);
						return storeName.startsWith(prefix) && !storeName.substring(prefix.length).includes("_");
					}
					const base = `${appCode}_${userId}`;
					if (store === "*") return storeName.startsWith(`${base}_`);
					if (store != null) return storeName === `${base}_${store}`;
					return storeName === base;
				};
				break;
			case BarCacheStoreType.Global:
				dbName = DB_NAME_GLOBAL;
				filterFn = (storeName) => {
					const { store } = options;
					if (store === "*") return storeName.startsWith("main_");
					if (store != null) return storeName === `main_${store}`;
					return storeName === "main";
				};
				break;
			default: throw new Error(`Unsupported store type: ${options.storeType}`);
		}
		return (await this.#getObjectStoreNames(dbName)).filter(filterFn).map((storeName) => this.#getStore(dbName, storeName));
	}
	async invalidate(clearSession = false) {
		if (this.#options.cacheStorage?.type === "none") return;
		const correlation = { value: generateUuid() };
		this.#logger.log({
			message: `Starting cache invalidation with argument clearSession = ${clearSession}`,
			correlation
		});
		await this.#invalidateDatabase(DB_NAME_APP, clearSession, correlation);
		await this.#invalidateDatabase(DB_NAME_USER, clearSession, correlation);
		await this.#invalidateDatabase(DB_NAME_APP_USER, clearSession, correlation);
		await this.#invalidateDatabase(DB_NAME_GLOBAL, clearSession, correlation);
		await this.#invalidateDatabase(DB_NAME_GVK, clearSession, correlation);
		this.#logger.log({
			message: "Ending cache invalidation",
			correlation
		});
	}
	async dropStores(options) {
		const stores = await this.getStores(options);
		for (const store of stores) await store.drop();
	}
	async clearStores(options) {
		const stores = await this.getStores(options);
		for (const store of stores) await store.clear();
	}
	async #invalidateDatabase(name, clearSession, correlation) {
		this.#logger.log({
			message: `Starting database invalidation with name '${name}'`,
			correlation
		});
		this.#logger.log({
			message: "Getting objectStoreNames for invalidation purposes",
			correlation
		});
		const objectStoreNames = await this.#getObjectStoreNames(name);
		this.#logger.log({
			message: "Retrieved objectStoreNames for invalidation purposes",
			correlation
		});
		for (const storeName of objectStoreNames) await this.#getStore(name, storeName, correlation).invalidate(clearSession);
		this.#logger.log({
			message: "Getting objectStoreNames for dropping purposes",
			correlation
		});
		const remainingObjectStoreNames = await this.#getObjectStoreNames(name);
		this.#logger.log({
			message: "Retrieved objectStoreNames for dropping purposes",
			correlation
		});
		if (remainingObjectStoreNames.length === 0) try {
			await indexedDB.deleteDatabase(name);
		} catch (error) {
			this.#logger.error({ message: `Error deleting database (db='${name}'): ${error}` });
			throw error;
		}
		else this.#logger.log({
			message: `Ending database invalidation with name '${name}'`,
			correlation
		});
	}
	async #getObjectStoreNames(name) {
		let db;
		try {
			db = await openDB(name);
			return Array.from(db.objectStoreNames);
		} catch (error) {
			this.#logger.error({ message: `Error opening database to read objectStoreNames (db='${name}'): ${error}` });
			throw error;
		} finally {
			db?.close();
		}
	}
	#getStore(name, storeName, correlation = void 0) {
		if (this.#options.cacheStorage?.type === "none") return new BaasNullCacheStore();
		const getContextData = () => {
			return { sessionId: this.#appState.sessionId };
		};
		return new BarCacheStore(name, storeName, getContextData, correlation);
	}
};
BarCacheManager = __decorate([injectable(), __metadata("design:paramtypes", [BaasAppState, BaasBootstrapOptions])], BarCacheManager);
var BarQueueProcessor = class {
	constructor(storage, storageKey) {
		this.storage = storage;
		this.storageKey = storageKey;
		this.started = false;
	}
	start(interval, dequeueCount) {
		this.intervalId = window.setInterval(() => this.dequeue(dequeueCount), interval);
		this.started = true;
	}
	stop() {
		if (this.started) {
			this.started = false;
			window.clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}
	enqueue(item) {
		let queueStr = this.storage.getItem(this.storageKey);
		const queue = queueStr == null ? [] : JSON.parse(queueStr);
		queue.push(item);
		queueStr = JSON.stringify(queue);
		this.storage.setItem(this.storageKey, queueStr);
	}
	dequeue(dequeueCount) {
		let queueStr = this.storage.getItem(this.storageKey);
		if (queueStr != null) {
			let queue = JSON.parse(queueStr);
			if (queue.length > 0) {
				const itemsToSend = queue.slice(0, dequeueCount);
				queue = queue.slice(dequeueCount);
				if (queue.length == 0) this.storage.removeItem(this.storageKey);
				else {
					queueStr = JSON.stringify(queue);
					this.storage.setItem(this.storageKey, queueStr);
				}
				this.onExecute(itemsToSend);
			}
		}
	}
};
const APP_INFO_COOKIE_KEY = "domain.basisarchitectuur.appinfo";
const STORAGE_KEY_PREFIX = "lvflanders.bar.shared.";
const OIDC_CALLBACK_PATH = "/auth/oidc/callback/";
const _BAAS_SHARED_CONSTANTS = {
	STORAGE_KEY_PREFIX,
	TEMP_STORAGE_STATE_KEY: STORAGE_KEY_PREFIX + "temp-state",
	DEFAULT_MEETPUNT_TYPE: "NG",
	STORAGE_KEY: {
		SESSION_ID: "sessionId",
		IDP_IMPERSONATE: STORAGE_KEY_PREFIX + "idpImpersonate",
		BAAS_STATE_CONTEXT: STORAGE_KEY_PREFIX + "baas-state-context",
		BAR_APPINFO_CONTEXT: STORAGE_KEY_PREFIX + "bar-appinfo-context"
	},
	URL_SEGMENT: {
		BAR_WEB: "/basisarchitectuur/domain.basisarchitectuur.web/",
		BAAS_STATIC: "/{segment}/baas-static/web/",
		BAAS_WEB: "/{segment}/baasweb/web/",
		IDP_OIDC: "/{segment}/idp-oidc/web/",
		STARTSCHERM: "/a/startscherm/v2/web/home"
	},
	AUDIT: {
		HTML_ATTRIBUTE_AUDIT_ID: "data-bar-audit-id",
		HTML_ATTRIBUTE_AUDIT_TYPE: "data-bar-audit-type"
	},
	COOKIE: { APP_INFO: {
		MAIN: APP_INFO_COOKIE_KEY,
		SESSION: APP_INFO_COOKIE_KEY + ".session",
		SECURE: APP_INFO_COOKIE_KEY + ".secure"
	} },
	HEADER: {
		BAAS_BACKEND_HEADER: "baas.context.backend",
		BAAS_BACKEND_HEADER_V2: "baas-context-backend",
		BAAS_BROWSER_CALLSTACK: "baas.browser.callstack",
		BAAS_BROWSER_CALLSTACK_V2: "baas-browser-callstack",
		BAAS_HOEDANIGHEID_TOKEN: "baas-hoedanigheid-token",
		BAAS_VOORWIE_TOKEN: "baas-voorwie-token"
	},
	PAGE: {
		LOG_OFF: "bui/log-off",
		INSUFFICIENT_PRIVILEGES: "bui/insufficient-privileges"
	},
	AUTH: { OIDC: { CALLBACK: {
		LV: OIDC_CALLBACK_PATH + "lv",
		SPOTFIRE: OIDC_CALLBACK_PATH + "spotfire"
	} } }
};
var BaasLogLevel;
(function(BaasLogLevel) {
	BaasLogLevel[BaasLogLevel["Debug"] = 0] = "Debug";
	BaasLogLevel[BaasLogLevel["Audit"] = 1] = "Audit";
	BaasLogLevel[BaasLogLevel["Info"] = 2] = "Info";
	BaasLogLevel[BaasLogLevel["Warn"] = 3] = "Warn";
	BaasLogLevel[BaasLogLevel["Error"] = 4] = "Error";
	BaasLogLevel[BaasLogLevel["Critical"] = 5] = "Critical";
})(BaasLogLevel || (BaasLogLevel = {}));
let BaasLoggingService = class BaasLoggingService {
	#loggingApiClient;
	#options;
	#state;
	#queueProcessor;
	constructor(loggingApiClient, options, state) {
		this.#loggingApiClient = loggingApiClient;
		this.#options = options;
		this.#state = state;
		this.#queueProcessor = new BarQueueProcessor(window.sessionStorage, `${_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX}logQueue`);
	}
	error(log) {
		this.#post(log, BaasLogLevel.Error);
	}
	warn(log) {
		this.#post(log, BaasLogLevel.Warn);
	}
	critical(log) {
		this.#post(log, BaasLogLevel.Critical);
	}
	info(log) {
		this.#post(log, BaasLogLevel.Info);
	}
	init() {
		const global = ensureGlobal("lvflanders.bar.shared.logging");
		Object.defineProperty(global, "failedLogs", {
			get() {
				const failedLogsStr = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "failedLogs");
				return failedLogsStr != null ? JSON.parse(failedLogsStr) : null;
			},
			configurable: true
		});
	}
	async start() {
		this.#queueProcessor.onExecute = async (logs) => {
			try {
				await this.#loggingApiClient.save(logs);
			} catch (error) {
				console.error(error);
				const failedLogsStr = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "failedLogs");
				const failedLogs = failedLogsStr != null ? JSON.parse(failedLogsStr) : [];
				logs.forEach((log) => {
					log.timestamp = /* @__PURE__ */ new Date();
					failedLogs.push(log);
				});
				window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "failedLogs", JSON.stringify(failedLogs));
			}
		};
		this.#queueProcessor.start(this.#state.config.log.interval, this.#state.config.log.itemCount);
	}
	#post(log, level) {
		if (!this.#state.config?.log.enabled) return;
		const model = {
			logLevel: level,
			message: log.message,
			appCode: void 0,
			appVersion: void 0
		};
		if (log.error != null) {
			if (typeof log.error === "string") {
				model.exception = log.error;
				if (model.message == null) model.message = log.error;
			} else {
				model.exception = log.error.message;
				model.stackTrace = log.error.stack;
				if (model.message == null) model.message = log.error.message;
			}
		}
		if (this.#state.toepassing != null) {
			model.appCode = this.#state.toepassing.code;
			model.appVersion = this.#state.toepassing.versie;
		} else {
			model.appCode = this.#options.appCode;
			model.appVersion = this.#options.appVersion;
		}
		if (!model.message) model.message = "";
		model.message += `\r\n\r\nURL: ${window.location.href}`;
		this.#queueProcessor.enqueue(model);
	}
};
BaasLoggingService = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.BaasLoggingApiClient)),
	__metadata("design:paramtypes", [
		Object,
		BaasBootstrapOptions,
		BaasAppState
	])
], BaasLoggingService);
const CACHE_STORE = "baas-static";
const CONFIGURATION_KEY = "configuration";
let _BaasStaticConfigurationApiClient = class _BaasStaticConfigurationApiClient {
	constructor(axios, axiosSilent, appState, logging, cacheManager) {
		this.axios = axios;
		this.axiosSilent = axiosSilent;
		this.appState = appState;
		this.logging = logging;
		this.cacheManager = cacheManager;
	}
	async getConfiguration() {
		return this.cacheManager.getStore({
			storeType: BarCacheStoreType.Global,
			store: CACHE_STORE
		}).getOrSetItem(CONFIGURATION_KEY, () => this.#executeGetConfiguration());
	}
	checkChatbotEnabled() {
		return Promise.resolve(this.appState.config?.baasStatic?.chatbot?.enabled);
	}
	async enableChatbot(enabled) {
		const url = concatUrl(this.appState.location.apiRoot.url, "api/configuration/chat-bot/enabled");
		await this.axios.put(url, enabled, { headers: { "Content-Type": "application/json" } });
		if (this.appState.config?.baasStatic?.chatbot) {
			this.appState.config.baasStatic.chatbot.enabled = enabled;
			await this.#invalidateConfiguration();
		}
	}
	async #executeGetConfiguration() {
		const url = concatUrl(this.appState.location.baasStatic.url, "api/configuration");
		try {
			return (await this.axiosSilent.get(url)).data;
		} catch (err) {
			console.error(err);
			this.logging.error(err);
			return;
		}
	}
	async #invalidateConfiguration() {
		await this.cacheManager.getStore({
			storeType: BarCacheStoreType.Global,
			store: CACHE_STORE
		}).removeItem(CONFIGURATION_KEY);
	}
};
_BaasStaticConfigurationApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__param(1, inject(BAAS_TYPES.AxiosInstance)),
	__param(1, named(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent)),
	__metadata("design:paramtypes", [
		Function,
		Function,
		BaasAppState,
		BaasLoggingService,
		BarCacheManager
	])
], _BaasStaticConfigurationApiClient);
var BarStopwatch = class {
	constructor() {
		this.id = null;
		this.duration = null;
	}
	start() {
		this.reset();
		if (window.performance && window.performance.timing) {
			this.id = generateUuid();
			window.performance.mark(this.id + "_start");
		}
	}
	stop() {
		if (window.performance && window.performance.timing && this.id != null) {
			window.performance.mark(this.id + "_end");
			window.performance.measure(this.id, this.id + "_start", this.id + "_end");
			const entries = window.performance.getEntriesByName(this.id);
			window.performance.clearMarks(this.id + "_end");
			window.performance.clearMeasures(this.id);
			this.reset();
			this.duration = entries[0].duration;
		}
	}
	reset() {
		if (this.id != null) {
			window.performance.clearMarks(this.id + "_start");
			this.id = null;
			this.duration = null;
		}
	}
};
function deepClone(item) {
	return deepCloneInternal(item, []);
}
function deepCloneInternal(item, traversed) {
	if (!item) return item;
	const types = [
		Number,
		String,
		Boolean
	];
	let result;
	types.forEach(function(type) {
		if (item instanceof type) result = type(item);
	});
	if (typeof result == "undefined") {
		if (Object.prototype.toString.call(item) === "[object Array]") {
			result = [];
			item.forEach(function(child, index, array) {
				result[index] = deepClone(child);
			});
		} else if (typeof item == "object") {
			if (item.nodeType && typeof item.cloneNode == "function") result = item.cloneNode(true);
			else if (!item.prototype) {
				if (item instanceof Date) result = new Date(item);
				else {
					const traversedItem = traversed.find((t) => t.originalItem == item);
					if (traversedItem != null) result = traversedItem.clonedItem;
					else {
						result = {};
						traversed.push({
							originalItem: item,
							clonedItem: result
						});
						for (const i in item) result[i] = deepCloneInternal(item[i], traversed);
					}
				}
			} else if (item.constructor) result = new item.constructor();
			else result = item;
		} else result = item;
	}
	return result;
}
function _addAuditInfoOnDocument(audit) {
	if (document && audit) {
		const htmlEl = document.getElementsByTagName("html");
		if (htmlEl.length > 0) {
			htmlEl[0].setAttribute(_BAAS_SHARED_CONSTANTS.AUDIT.HTML_ATTRIBUTE_AUDIT_ID, audit.auditId);
			htmlEl[0].setAttribute(_BAAS_SHARED_CONSTANTS.AUDIT.HTML_ATTRIBUTE_AUDIT_TYPE, audit.auditType);
		}
	}
}
let BaasAuditService = class BaasAuditService {
	constructor(logging, state, auditApiClient) {
		this.logging = logging;
		this.state = state;
		this.auditApiClient = auditApiClient;
		this.queueProcessor = new BarQueueProcessor(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "auditQueue");
	}
	log(audit) {
		if (this.state.config.audit.enabled !== true || this.state.config.audit.excludedAuditTypes != null && this.state.config.audit.excludedAuditTypes.indexOf(audit.auditType) > -1) return;
		if (audit.timestamp == null) audit.timestamp = /* @__PURE__ */ new Date();
		if (audit.sessionId == null) audit.sessionId = this.state.sessionId;
		if (audit.appCode == null && this.state.toepassing != null) audit.appCode = this.state.toepassing.code;
		if (this.state.basiscontext != null) {
			audit.hoedanigheid = this.state.basiscontext.hoedanigheid;
			audit.hoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode;
			audit.voorwie = this.state.basiscontext.voorwie;
			audit.voorwieRolcode = this.state.basiscontext.voorwieRolcode;
			audit.userId = this.state.basiscontext.userId;
		}
		audit.hostName = location.hostname;
		if (!audit.details) audit.details = {};
		if (!audit.details["PGID"]) audit.details["PGID"] = this.state.pageId;
		this.queueProcessor.enqueue(audit);
	}
	start() {
		this.queueProcessor.onExecute = async (auditItems) => {
			try {
				await this.auditApiClient.save(auditItems);
			} catch (error) {
				console.error(error);
				this.logging.error({ error });
			}
		};
		this.queueProcessor.start(this.state.config.audit.interval, this.state.config.audit.itemCount);
	}
};
BaasAuditService = __decorate([
	injectable(),
	__param(2, inject(BAAS_TYPES.BaasAuditApiClient)),
	__metadata("design:paramtypes", [
		BaasLoggingService,
		BaasAppState,
		Object
	])
], BaasAuditService);
var BaasAuditType;
(function(BaasAuditType) {
	BaasAuditType["Session"] = "BROWSERSESSIONSTART";
	BaasAuditType["Page"] = "BROWSERPAGE";
	BaasAuditType["Route"] = "BROWSERROUTE";
	BaasAuditType["Dependencies"] = "BROWSERDEPENDENCIES";
})(BaasAuditType || (BaasAuditType = {}));
let BaasConfigurationService = class BaasConfigurationService {
	constructor(state, cacheManager, apiClient) {
		this.state = state;
		this.cacheManager = cacheManager;
		this.apiClient = apiClient;
	}
	get store() {
		return this.cacheManager.getStore({
			storeType: BarCacheStoreType.User,
			store: "configuration",
			userId: this.state.basiscontext.userId
		});
	}
	readValue(options) {
		if (!options) throw new Error("The options argument is required.");
		if (!options.pathAndKey) throw new Error("The pathAndKey property is required.");
		let key = options.pathAndKey;
		if (key && key.indexOf("/") > -1) {
			console.warn("BaaS configuration keys should contain backslashes (\\) instead of slashes (/). The key was automatically transformed.");
			do
				key = key.replace("/", "\\");
			while (key.indexOf("/") > -1);
		}
		const httpCallback = () => {
			const model = {
				key,
				appCode: options.appCode || this.state.toepassing.code,
				appVersion: options.appVersion || this.state.toepassing.versie
			};
			return this.apiClient.read(model);
		};
		return this.store.getOrSetItem(key, httpCallback, { session: true });
	}
	flush() {
		return this.store.clear();
	}
};
BaasConfigurationService = __decorate([
	injectable(),
	__param(2, inject(BAAS_TYPES.BaasConfigurationApiClient)),
	__metadata("design:paramtypes", [
		BaasAppState,
		BarCacheManager,
		Object
	])
], BaasConfigurationService);
const LOCALE = {
	FLEMISH: "nl-be",
	DUTCH: "nl"
};
const BAAS_METADATA_KEY = { CACHE_STORE_TYPE_TAG: "cacheStoreType" };
const BAAS_CUSTOM_EVENT = {
	BASISCONTEXT_CHANGED: "basisContextChanged",
	CUSTOM_HUB_MESSAGE: "customHubMessage"
};
function clearFromStorage(storage, condition) {
	for (let i = storage.length - 1; i >= 0; i--) {
		const key = storage.key(i);
		if (key && condition(key)) storage.removeItem(key);
	}
}
var BaasActie = class {
	constructor(actie) {
		if (actie != null) {
			this.actie = actie.actie;
			this.voorwie = actie.voorwie;
			this.voorwieRolcode = actie.voorwieRolcode;
			this.hoedanigheid = actie.hoedanigheid;
			this.hoedanigheidRolcode = actie.hoedanigheidRolcode;
		}
	}
};
const TEMP_STATE_ROOT_KEY = `${_BAAS_SHARED_CONSTANTS.TEMP_STORAGE_STATE_KEY}.actie-allowed`;
let BaasSecurityService = class BaasSecurityService {
	get #tempStateKey() {
		let result = TEMP_STATE_ROOT_KEY;
		if (this.state.basiscontext?.userId) result += `.${this.state.basiscontext.userId}`;
		return result;
	}
	constructor(securityApiClient, state) {
		this.securityApiClient = securityApiClient;
		this.state = state;
	}
	async checkActieAllowed(actie) {
		let storageObj = this.#getActieAllowedStorageObj();
		const key = JSON.stringify(actie);
		if (storageObj[key] != null) return storageObj[key];
		else {
			const result = await this.securityApiClient.checkActieAllowed(actie);
			storageObj = this.#getActieAllowedStorageObj();
			storageObj[key] = result;
			localStorage.setItem(this.#tempStateKey, JSON.stringify(storageObj));
			return result;
		}
	}
	checkActieAllowedFromContext(actie) {
		const actieObj = new BaasActie();
		actieObj.actie = actie.actie;
		if (actie.includeVoorwie) {
			actieObj.voorwie = this.state.basiscontext.voorwie;
			actieObj.voorwieRolcode = this.state.basiscontext.voorwieRolcode;
		}
		if (actie.includeHoedanigheid) {
			actieObj.hoedanigheid = this.state.basiscontext.hoedanigheid;
			actieObj.hoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode;
		}
		return this.checkActieAllowed(actieObj);
	}
	flush() {
		clearFromStorage(localStorage, (key) => key.startsWith(TEMP_STATE_ROOT_KEY));
		return Promise.resolve();
	}
	#getActieAllowedStorageObj() {
		const storageObjStr = localStorage.getItem(this.#tempStateKey);
		return storageObjStr ? JSON.parse(storageObjStr) : {};
	}
};
BaasSecurityService = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.BaasSecurityApiClient)),
	__metadata("design:paramtypes", [Object, BaasAppState])
], BaasSecurityService);
const STORAGE_KEY = _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "requestLogOff";
let _BaasLogOffUtility = class _BaasLogOffUtility {
	constructor(options, state, configuration, security, globalStore, logOffHooks) {
		this.options = options;
		this.state = state;
		this.configuration = configuration;
		this.security = security;
		this.globalStore = globalStore;
		this.logOffHooks = logOffHooks;
	}
	startListening() {
		window.onstorage = () => {
			if (this.requestLogOff) this.navigateLogOff();
		};
	}
	get requestLogOff() {
		return localStorage.getItem(STORAGE_KEY) === "true";
	}
	set requestLogOff(value) {
		if (value == null) localStorage.removeItem(STORAGE_KEY);
		else localStorage.setItem(STORAGE_KEY, value.toString());
	}
	async invokePreLogOffHooks() {
		if (!this.logOffHooks || this.logOffHooks.length == 0) return;
		this.logOffHooks.forEach(async (logOffHook) => {
			await logOffHook.run();
		});
	}
	async cleanup() {
		await this.globalStore.clear();
		if (this.state.basiscontext?.userId) {
			await this.configuration.flush();
			await this.security.flush();
		}
	}
	navigateLogOff() {
		window.location.href = this.options.auth?.logOffUrl || this.state.location.baasStatic.url + _BAAS_SHARED_CONSTANTS.PAGE.LOG_OFF;
	}
};
_BaasLogOffUtility = __decorate([
	injectable(),
	__param(4, inject(BarCacheStore)),
	__param(4, tagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global)),
	__param(5, multiInject(BAAS_TYPES.BaasLogOffHook)),
	__param(5, optional()),
	__metadata("design:paramtypes", [
		BaasBootstrapOptions,
		BaasAppState,
		BaasConfigurationService,
		BaasSecurityService,
		BarCacheStore,
		Array
	])
], _BaasLogOffUtility);
let BaasAuthService = class BaasAuthService {
	get authenticated() {
		return this.strategy.authenticated;
	}
	get authenticating() {
		return this.strategy.authenticating === true;
	}
	constructor(logOffService, strategy) {
		this.logOffService = logOffService;
		this.strategy = strategy;
	}
	authenticate() {
		return this.strategy.authenticate();
	}
	async postProcessing() {
		if (this.strategy.postProcessing) await this.strategy.postProcessing();
	}
	async logOff() {
		await this.logOffService.cleanup();
		await this.logOffService.invokePreLogOffHooks();
		this.logOffService.requestLogOff = true;
		await this.strategy.performLogOff();
	}
};
BaasAuthService = __decorate([
	injectable(),
	__param(1, inject(BAAS_TYPES.BaasAuthStrategy)),
	__metadata("design:paramtypes", [_BaasLogOffUtility, Object])
], BaasAuthService);
var TokenType;
(function(TokenType) {
	TokenType[TokenType["BarSecTicket"] = 0] = "BarSecTicket";
	TokenType[TokenType["OidcLinkId"] = 1] = "OidcLinkId";
})(TokenType || (TokenType = {}));
var BarAppInfo = class {};
let _BaasContextManager = class _BaasContextManager {
	saveBaasContextToStorage(baasPersistentStateObject) {
		if (typeof baasPersistentStateObject.tokenType === "string") switch (baasPersistentStateObject.tokenType.toUpperCase()) {
			case "BARSECTICKET":
				baasPersistentStateObject.tokenType = TokenType.BarSecTicket;
				break;
			case "OIDCLINKID":
				baasPersistentStateObject.tokenType = TokenType.OidcLinkId;
				break;
			default: baasPersistentStateObject.tokenType = void 0;
		}
		const jsonObj = JSON.stringify(baasPersistentStateObject);
		window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, jsonObj);
		window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, jsonObj);
	}
	updateAppInfoToStorage(appInfo) {
		if (!this.checkBaasContextInStorage()) return;
		const data = this.getBaasContextFromStorage();
		const vw = appInfo.ToepVoorwie ?? "";
		const vwrc = appInfo.ToepVoorwieRolcode ?? "";
		const hd = appInfo.ToepHoedanigheid ?? "";
		const hdrc = appInfo.ToepHoedanigheidRolcode ?? "";
		data.userVoorwie = `${vw};${vwrc}`;
		data.userHoedanigheid = `${hd};${hdrc}`;
		data.userID = appInfo?.ToepUser ?? data.userID;
		data.userTicketGuid = appInfo?.SecurityTicketGuid ?? data.userTicketGuid;
		if (data.userTicketInfo == null) data.userTicketInfo = {};
		data.userTicketInfo.userCbiUserID = appInfo.CbiUserId;
		window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, JSON.stringify(data));
		window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT);
		window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, JSON.stringify(data));
	}
	checkBaasContextInStorage() {
		return this.#getBaasContextString() !== null;
	}
	getAppInfoFromStorage() {
		const appInfoDataFromStorage = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT);
		if (appInfoDataFromStorage !== null) return JSON.parse(appInfoDataFromStorage);
		const data = this.getBaasContextFromStorage();
		const appinfo = this.#convertFromBaasState(data);
		window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT, JSON.stringify(appinfo));
		return appinfo;
	}
	removeContextFromSession() {
		window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
		window.sessionStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAR_APPINFO_CONTEXT);
		window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
	}
	getBaasContextFromStorage() {
		const data = this.#getBaasContextString();
		if (data === null) return null;
		return JSON.parse(data);
	}
	#convertFromBaasState(baas) {
		const resp = new BarAppInfo();
		resp.AuditId = baas.auditID;
		resp.CbiUserId = baas.userTicketInfo?.userCbiUserID;
		resp.SecurityTicketGuid = baas.userTicketGuid;
		resp.UserZone = baas.userTicketInfo?.userZone;
		resp.ForcePerformanceLog = false;
		resp.AllowCookieAuth = false;
		resp.ExpireTicks = this.#generateDatum2DaysInFutureInticks();
		resp.Timestamp = (/* @__PURE__ */ new Date()).toString();
		resp.ToepVoorwie = this.#getPart(true, baas?.userVoorwie);
		resp.ToepVoorwieRolcode = this.#getPart(false, baas?.userVoorwie);
		resp.ToepHoedanigheid = this.#getPart(true, baas?.userHoedanigheid);
		resp.ToepHoedanigheidRolcode = this.#getPart(false, baas?.userHoedanigheid);
		resp.ToepUser = baas?.userID;
		return resp;
	}
	#generateDatum2DaysInFutureInticks() {
		const date = /* @__PURE__ */ new Date();
		date.setTime(date.getTime() + 18e7);
		return date.getTime() * 1e4 + 0x89f7ff5f7b58000;
	}
	#getPart(getfirstPart, input) {
		if (input == null) return "";
		if (input.indexOf(";") == -1) return "";
		const segments = input.split(";");
		if (segments.length != 2) return "";
		return getfirstPart ? segments[0]?.trim() : segments[1]?.trim();
	}
	#getBaasContextString() {
		let data = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
		if (data === null) {
			data = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
			if (data !== null) window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT, data);
		}
		return data;
	}
};
_BaasContextManager = __decorate([injectable()], _BaasContextManager);
function toLowerCamelCaseKey(key) {
	if (!key) return key;
	const firstChar = key.charAt(0);
	if (firstChar === firstChar.toUpperCase() && firstChar !== firstChar.toLowerCase()) return firstChar.toLowerCase() + key.slice(1);
	return key;
}
function transformToLowerCamelCase(obj) {
	if (Array.isArray(obj)) return obj.map(transformToLowerCamelCase);
	else if (obj !== null && typeof obj === "object") return Object.keys(obj).reduce((acc, key) => {
		const newKey = toLowerCamelCaseKey(key);
		acc[newKey] = transformToLowerCamelCase(obj[key]);
		return acc;
	}, {});
	return obj;
}
let _BaasWebContextApiClient = class _BaasWebContextApiClient {
	#axios;
	#appState;
	constructor(axios, appState) {
		this.#axios = axios;
		this.#appState = appState;
	}
	async getStateContext() {
		const url = concatUrl(this.#appState.location.baasWeb.url, "api/script-helper/provide-info");
		return transformToLowerCamelCase((await this.#axios.get(url)).data);
	}
	async getStateContextByTicketId(securityTicketId) {
		const url = concatUrl(this.#appState.location.baasWeb.url, "api/script-helper-with-user/provide-header", securityTicketId);
		return transformToLowerCamelCase((await this.#axios.get(url)).data);
	}
};
_BaasWebContextApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], _BaasWebContextApiClient);
var BaasSecurityLevel;
(function(BaasSecurityLevel) {
	BaasSecurityLevel[BaasSecurityLevel["Development"] = 0] = "Development";
	BaasSecurityLevel[BaasSecurityLevel["Test"] = 1] = "Test";
	BaasSecurityLevel[BaasSecurityLevel["Production"] = 2] = "Production";
	BaasSecurityLevel[BaasSecurityLevel["Unknown"] = 3] = "Unknown";
})(BaasSecurityLevel || (BaasSecurityLevel = {}));
var BaasContextTransportModus;
(function(BaasContextTransportModus) {
	BaasContextTransportModus["Cookie"] = "Cookie";
	BaasContextTransportModus["Header"] = "Header";
})(BaasContextTransportModus || (BaasContextTransportModus = {}));
var BarAppInfoSecure = class {};
let _BarAppInfoManager = class _BarAppInfoManager {
	#data;
	get sessionId() {
		return this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SESSION);
	}
	set sessionId(sessionId) {
		this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SESSION, sessionId);
	}
	get #secureCookieMissing() {
		return this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE) === null;
	}
	constructor(state, baasContextService, options) {
		this.state = state;
		this.baasContextService = baasContextService;
		this.options = options;
	}
	init(data) {
		this.#data = data;
	}
	get() {
		if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header) {
			if (this.baasContextService.checkBaasContextInStorage()) return this.baasContextService.getAppInfoFromStorage();
			return null;
		}
		const koek = this.#readCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN);
		if (koek == null) return null;
		const appInfo = JSON.parse(decodeURIComponent(koek));
		if (!appInfo.SecurityTicketGuid) appInfo.SecurityTicketGuid = this.#data?.state?.userTicketGuid;
		return appInfo;
	}
	delete() {
		this.baasContextService.removeContextFromSession();
		if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header) return;
		deleteCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN);
		deleteCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE);
	}
	async save(appInfo, createGuidCookie = false, setTimestamp = true) {
		if (setTimestamp) appInfo.Timestamp = (/* @__PURE__ */ new Date()).toISOString();
		if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header) {
			this.baasContextService.updateAppInfoToStorage(appInfo);
			return;
		}
		this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.MAIN, JSON.stringify(appInfo));
		if (createGuidCookie || this.#secureCookieMissing) {
			const secureContent = new BarAppInfoSecure();
			secureContent.SecurityTicketGuid = appInfo.SecurityTicketGuid;
			secureContent.ExpireTicks = appInfo.ExpireTicks;
			this.#createCookie(_BAAS_SHARED_CONSTANTS.COOKIE.APP_INFO.SECURE, JSON.stringify(secureContent));
		}
	}
	async persistBasiscontext() {
		if (this.state.basiscontext == null) return;
		const appInfo = this.get();
		if (appInfo != null) {
			appInfo.ToepVoorwie = this.state.basiscontext.voorwie == null ? "" : this.state.basiscontext.voorwie;
			appInfo.ToepVoorwieRolcode = this.state.basiscontext.voorwieRolcode == null ? "" : this.state.basiscontext.voorwieRolcode;
			appInfo.ToepHoedanigheid = this.state.basiscontext.hoedanigheid == null ? "" : this.state.basiscontext.hoedanigheid;
			appInfo.ToepHoedanigheidRolcode = this.state.basiscontext.hoedanigheidRolcode == null ? "" : this.state.basiscontext.hoedanigheidRolcode;
			await this.save(appInfo, false, true);
		}
	}
	getBasiscontext() {
		const appInfo = this.get();
		return this.#toBasiscontext(appInfo);
	}
	#toBasiscontext(appInfo) {
		let result = null;
		if (appInfo != null) {
			result = {};
			const hoedanigheidKey = "hoedanigheid";
			result[hoedanigheidKey] = appInfo.ToepHoedanigheid;
			const hoedanigheidRolcodeKey = "hoedanigheidRolcode";
			result[hoedanigheidRolcodeKey] = appInfo.ToepHoedanigheidRolcode;
			const voorwieKey = "voorwie";
			result[voorwieKey] = appInfo.ToepVoorwie;
			const voorwieRolcodeKey = "voorwieRolcode";
			result[voorwieRolcodeKey] = appInfo.ToepVoorwieRolcode;
			const userIdKey = "userId";
			result[userIdKey] = appInfo.ToepUser;
			const securityTicketIdKey = "securityTicketId";
			result[securityTicketIdKey] = appInfo.SecurityTicketGuid || "";
			const zoneKey = "zone";
			result[zoneKey] = appInfo.UserZone || appInfo.ToepZone;
			if (this.#data != null) {
				const fullUsernameKey = "fullUsername";
				result[fullUsernameKey] = this.#data.fullUsername;
				const klantnummerKey = "klantnummer";
				result[klantnummerKey] = this.#data.klantnummer;
				if (result.userId != null && result.userId.toString().startsWith("DUMMY")) {
					const testProfielKey = "testProfiel";
					try {
						result[testProfielKey] = result.fullUsername.substr(result.fullUsername.indexOf("\\") + 1, result.fullUsername.indexOf("#") - 6);
					} catch (error) {
						console.warn("BarAppInfoService: ", error);
					}
				}
			}
			const impersonateStrStorage = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
			let impersonate = false;
			if (impersonateStrStorage != null && impersonateStrStorage.length) impersonate = impersonateStrStorage.toLowerCase() === true.toString();
			const impersonateKey = "impersonate";
			result[impersonateKey] = impersonate;
		}
		return result;
	}
	#readCookie(name) {
		const nameEQ = name + "=";
		const ca = document.cookie.split(";");
		for (const c of ca) {
			let cookie = c;
			while (cookie.charAt(0) === " ") cookie = cookie.substring(1, cookie.length);
			if (cookie.indexOf(nameEQ) === 0) {
				const cookieValue = decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
				return cookieValue === "null" ? null : cookieValue;
			}
		}
		return null;
	}
	#createCookie(name, value) {
		let str = name + "=" + encodeURIComponent(value) + ";path=/;";
		if (this.state.toepassing != null && this.state.toepassing.securityLevel != BaasSecurityLevel.Production) str = str + "SameSite=None;Secure=true;";
		document.cookie = str;
	}
};
_BarAppInfoManager = __decorate([injectable(), __metadata("design:paramtypes", [
	BaasAppState,
	_BaasContextManager,
	BaasBootstrapOptions
])], _BarAppInfoManager);
let BaasContextService = class BaasContextService {
	#apiClient;
	#contextManager;
	#appInfo;
	constructor(apiClient, contextManager, appInfo) {
		this.#apiClient = apiClient;
		this.#contextManager = contextManager;
		this.#appInfo = appInfo;
	}
	async getStateContext() {
		const context = this.#contextManager.getBaasContextFromStorage();
		if (context) return context;
		const appInfo = this.#appInfo.get();
		if (appInfo) return {
			appCode: appInfo.ToepCode,
			appOmgeving: appInfo.ToepSecurityEnvironment,
			appVersion: appInfo.ToepVersion,
			auditID: appInfo.AuditId,
			auditType: null,
			originalUserID: appInfo.OriginalUserID,
			tokenType: TokenType.BarSecTicket,
			userID: appInfo.ToepUser,
			userTicketGuid: appInfo.SecurityTicketGuid,
			userTicketInfo: {
				userTicketName: appInfo.ToepUser,
				userCbiUserID: appInfo.CbiUserId
			},
			userVoorwie: `${appInfo.ToepVoorwie};${appInfo.ToepVoorwieRolcode}`,
			userHoedanigheid: `${appInfo.ToepHoedanigheid};${appInfo.ToepHoedanigheidRolcode}`
		};
		return await this.#apiClient.getStateContext();
	}
};
BaasContextService = __decorate([injectable(), __metadata("design:paramtypes", [
	_BaasWebContextApiClient,
	_BaasContextManager,
	_BarAppInfoManager
])], BaasContextService);
function generateBaasAuthHeaders(securityContext) {
	const headers = {};
	if (securityContext) {
		const contextValue = JSON.stringify(securityContext);
		headers[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BACKEND_HEADER] = contextValue;
		headers[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BACKEND_HEADER_V2] = contextValue;
	}
	return encodeHeaders(headers);
}
let BaasOidcAuthService = class BaasOidcAuthService {
	#options;
	#_userManager;
	get #userManager() {
		if (this.#_userManager === void 0) throw new Error("Oidc auth service has not been initialized. Call init() first.");
		return this.#_userManager;
	}
	init(options) {
		if (!options) throw new Error("Options are required to initialize the Oidc auth service.");
		if (this.#_userManager !== void 0) throw new Error("Oidc auth service is already initialized.");
		this.#options = options;
		const settings = {
			authority: options.authority,
			client_id: options.clientId,
			redirect_uri: options.callbackUri,
			response_type: "code",
			scope: options.clientScope,
			userStore: new WebStorageStateStore({ store: window.localStorage }),
			fetchRequestCredentials: "include",
			automaticSilentRenew: true,
			metadataUrl: options.metadataUrl
		};
		this.#_userManager = new UserManager(settings);
		this.#_userManager.events.addUserLoaded((user) => {
			if (options.userAddedCallback) options.userAddedCallback(user);
		});
	}
	getUser() {
		return this.#userManager.getUser();
	}
	login(identityType = "windows") {
		return this.#userManager.signinRedirect({
			extraQueryParams: { identitytype: identityType },
			state: { targetUrl: window.location.href }
		});
	}
	logout() {
		return this.#userManager.signoutSilent();
	}
	renewToken(identityType = "windows") {
		return this.#userManager.signinSilent({ extraQueryParams: { identitytype: identityType } });
	}
	async callback() {
		const state = (await this.#userManager.signinCallback())?.state;
		if (state?.targetUrl) window.location.href = state.targetUrl;
		else window.location.href = this.#options.defaultLandingPage;
	}
};
BaasOidcAuthService = __decorate([injectable()], BaasOidcAuthService);
let _BaasStaticApiClient = class _BaasStaticApiClient {
	#appState;
	#axiosPlain;
	constructor(axiosPlain, appState) {
		this.#axiosPlain = axiosPlain;
		this.#appState = appState;
	}
	async resetState() {
		const url = concatUrl(this.#appState.location.baasStatic.url, "api/state/reset");
		await this.#axiosPlain.get(url, { withCredentials: true });
	}
};
_BaasStaticApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], _BaasStaticApiClient);
let BaasSession = class BaasSession {
	#baasStaticApiClient;
	constructor(baasStaticApiClient) {
		this.#baasStaticApiClient = baasStaticApiClient;
	}
	async reset() {
		window.localStorage.clear();
		window.sessionStorage.clear();
		await deleteAllIndexedDb();
		deleteAllCookies();
		await this.#baasStaticApiClient.resetState();
	}
};
BaasSession = __decorate([
	injectable(),
	__param(0, inject(_BaasStaticApiClient)),
	__metadata("design:paramtypes", [_BaasStaticApiClient])
], BaasSession);
var BarIdpError = class extends Error {
	constructor(message) {
		super(message);
		this.name = this.constructor.name;
		if (arguments.length > 1) this.status = arguments[1];
	}
};
var BarIdpClientError = class extends BarIdpError {};
var BarIdpClientOptions = class {};
var BarAuthValues = class {};
var BarIdentity = class {};
var BarKlant = class {};
var BarIdentityType;
(function(BarIdentityType) {
	BarIdentityType["Anoniem"] = "ANONIEM";
	BarIdentityType["CID"] = "CID";
	BarIdentityType["Windows"] = "WINDOWS";
	BarIdentityType["Cryptokey"] = "CRYPTOKEY";
	BarIdentityType["Klantnummer"] = "ALVNUMMER";
	BarIdentityType["Testprofiel"] = "TESTPROFIEL";
	BarIdentityType["Fas"] = "FAS";
})(BarIdentityType || (BarIdentityType = {}));
var BarIdpWebworkerOrchestrator = class {
	constructor(idpUrl, wwUrl) {
		this.idpUrl = idpUrl;
		this.wwUrl = wwUrl;
		this.processMessage = function(event) {
			if (event) {
				const parsedData = JSON.parse(event.data);
				switch (Object.keys(parsedData)[0]) {
					case "INFO":
						if (parsedData.INFO) console.info(`webworker INFO message : ${parsedData.INFO.message}`);
						break;
					case "WARNING":
						console.warn(`webworker WARNING message: ${parsedData.WARNING.message}`);
						break;
					case "ERROR": console.error(parsedData.ERROR);
				}
			}
		};
		this.processError = function(errorEvent) {
			console.error(errorEvent);
			if (window.Worker && typeof this.idpww != "undefined" && typeof this.idpww.terminate == "function") this.idpww.terminate();
		};
	}
	init(timeout) {
		try {
			if (window.Worker) {
				if (typeof this.idpww == "undefined") {
					let workerUrl = checkLocalhost() ? window.location.origin : this.wwUrl;
					if (!workerUrl.endsWith("/")) workerUrl += "/";
					workerUrl += "scripts/idp-validation-worker/main.js";
					this.idpww = new Worker(workerUrl);
					if (typeof this.idpww.postMessage == "function") {
						if (timeout != null) this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
						this.idpww.postMessage(JSON.stringify({ INIT: {
							idpUrl: this.idpUrl,
							timeout
						} }));
					} else console.warn("initWebworker : Er kon geen message verstuurd worden naar de webworker");
					this.idpww.onmessage = (ev) => this.processMessage(ev);
					this.idpww.onerror = (ev) => this.processError(ev);
				} else console.warn("initWebworker : initialisatie niet uitgevoerd, we hebben reeds een webworker");
			} else {
				document.getElementById("result").innerHTML = "Sorry, uw browser ondersteunt geen Web Workers...";
				console.error("Sorry, uw browser ondersteunt geen Web Workers...");
			}
		} catch (error) {
			console.error(error);
		}
	}
	setTimeout(timeout) {
		try {
			if (window.Worker && typeof this.idpww != "undefined" && typeof this.idpww.postMessage == "function") this.idpww.postMessage(JSON.stringify({ SETTIMEOUT: timeout }));
			else console.warn("setTimeout : Er kon geen message verstuurd worden naar de webworker");
		} catch {
			console.error("er ging iets mis voor setTimeout " + timeout);
		}
	}
	getTimeout() {
		try {
			if (window.Worker && typeof this.idpww != "undefined" && typeof this.idpww.postMessage == "function") this.idpww.postMessage(JSON.stringify({ GETTIMEOUT: true }));
			else console.warn("getTimeout : Er kon geen message verstuurd worden naar de webworker");
		} catch (error) {
			console.error(error);
		}
	}
};
let BarIdpClient = class BarIdpClient {
	#wwMgr;
	#appInfoMgr;
	get authenticated() {
		return this.#appInfoCookie != null;
	}
	get #authUrl() {
		return concatUrl(this.options.idpUrl, "/auth/token");
	}
	get #logoutUrl() {
		return concatUrl(this.options.idpUrl, "/auth/logout");
	}
	get #appInfoCookie() {
		return this.#appInfoMgr.get();
	}
	constructor(options = new BarIdpClientOptions(), appInfoMgr) {
		this.options = options;
		this.appInfoMgr = appInfoMgr;
		this.#appInfoMgr = appInfoMgr;
	}
	async authenticate(config = {
		identity: void 0,
		target: this.options.targetUrl
	}) {
		if (!config.identity) {
			if (this.options.zone === "E") config.identity = { type: BarIdentityType.Fas };
			else config.identity = { type: BarIdentityType.Windows };
		}
		if (!this.options.idpUrl) throw new BarIdpClientError("De idpUrl is niet gedefinieerd.");
		let authUrl = this.#authUrl;
		authUrl = this.#appendQuerystring(authUrl, "target", config.target || window.location.href);
		if (!config || !config.identity || !config.identity.type) throw Error("Fatale fout : er werd geen identiteitstype meegegeven");
		else authUrl = this.#appendQuerystring(authUrl, "identityType", config.identity.type);
		if (config && config.identity && config.identity.value) authUrl = this.#appendQuerystring(authUrl, "identityValue", config.identity.value);
		if (config && config.voorwie && config.voorwie.rolcode) authUrl = this.#appendQuerystring(authUrl, "vw", config.voorwie.klantnummer);
		if (config && config.voorwie && config.voorwie.rolcode) authUrl = this.#appendQuerystring(authUrl, "vwrc", config.voorwie.rolcode);
		if (config && config.voorwie && config.hoedanigheid.klantnummer) authUrl = this.#appendQuerystring(authUrl, "vw", config.hoedanigheid.klantnummer);
		if (config && config.voorwie && config.hoedanigheid.rolcode) authUrl = this.#appendQuerystring(authUrl, "vwrc", config.hoedanigheid.rolcode);
		if (this.options.formatToken) authUrl = this.#appendQuerystring(authUrl, "formattoken", this.options.formatToken);
		window.sessionStorage?.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
		window.localStorage?.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.BAAS_STATE_CONTEXT);
		window.location.replace(authUrl);
	}
	authenticateWithAlvnr(identityValue) {
		return this.authenticate({
			identity: {
				type: BarIdentityType.Klantnummer,
				value: identityValue
			},
			target: this.options.targetUrl
		});
	}
	authenticateWithAnoniem() {
		return this.authenticate({
			identity: { type: BarIdentityType.Anoniem },
			target: this.options.targetUrl
		});
	}
	authenticateWithCBIuserId(identityValue) {
		return this.authenticate({
			identity: {
				type: BarIdentityType.CID,
				value: identityValue
			},
			target: this.options.targetUrl
		});
	}
	authenticateWithCryptoKey(identityValue) {
		return this.authenticate({
			identity: {
				type: BarIdentityType.Cryptokey,
				value: identityValue
			},
			target: this.options.targetUrl
		});
	}
	authenticateWithWindows(identityValue) {
		return this.authenticate({
			identity: {
				type: BarIdentityType.Windows,
				value: identityValue
			},
			target: this.options.targetUrl
		});
	}
	authenticateWithFas() {
		return this.authenticate({
			identity: { type: BarIdentityType.Fas },
			target: this.options.targetUrl
		});
	}
	authenticateWithTestProfiel(identityValue) {
		return this.authenticate({
			identity: {
				type: BarIdentityType.Testprofiel,
				value: identityValue
			},
			target: this.options.targetUrl
		});
	}
	validate(timeout) {
		if (!this.options.wwUrl) throw new BarIdpClientError("De web worker URL is niet gedefinieerd.");
		this.#wwMgr = new BarIdpWebworkerOrchestrator(this.options.idpUrl, this.options.wwUrl);
		if (timeout != null) this.#wwMgr.init(timeout);
		else this.#wwMgr.init();
	}
	logout(target) {
		let logoutUrl = this.#logoutUrl;
		if (target) {
			if (!checkUrlAbsolute(target)) target = concatUrl(window.location.origin, target);
			logoutUrl += `?target=${target}`;
		}
		window.location.replace(logoutUrl);
		return Promise.resolve();
	}
	#appendQuerystring(url, qsPrm, sqValue) {
		return url + (url.indexOf("?") > 0 ? "&" : "?") + qsPrm + "=" + sqValue;
	}
};
BarIdpClient = __decorate([injectable(), __metadata("design:paramtypes", [Object, _BarAppInfoManager])], BarIdpClient);
var _OopsType;
(function(_OopsType) {
	_OopsType[_OopsType["Technical"] = 0] = "Technical";
	_OopsType[_OopsType["Authentication"] = 1] = "Authentication";
	_OopsType[_OopsType["NotFound"] = 2] = "NotFound";
})(_OopsType || (_OopsType = {}));
const ACTION_ICON_REFRESH = "fa-solid fa-rotate";
const ACTION_CONTENT_REFRESH = "Herladen";
const ACTION_INFO_REFRESH = "Klik op herladen om opnieuw aan te melden.";
let _OopsDomService = class _OopsDomService {
	#appState;
	#baasSession;
	#appInfoManager;
	#bootstrapOptions;
	#mappings = {
		[_OopsType.Technical]: {
			title: "Technische fout",
			content: "Er heeft zich een technische fout voorgedaan.",
			action: this.#oopsActionRefresh.bind(this),
			actionIcon: ACTION_ICON_REFRESH,
			actionContent: ACTION_CONTENT_REFRESH,
			actionInfo: ACTION_INFO_REFRESH
		},
		[_OopsType.Authentication]: {
			title: "Sessie verlopen",
			content: "Uw huidige sessie is verlopen.",
			action: this.#oopsActionRefresh.bind(this),
			actionIcon: ACTION_ICON_REFRESH,
			actionContent: ACTION_CONTENT_REFRESH,
			actionInfo: ACTION_INFO_REFRESH
		},
		[_OopsType.NotFound]: {
			title: "Oeps",
			content: "Deze pagina is vermist.",
			action: this.#oopsActionHome.bind(this),
			actionIcon: "fa-regular fa-house",
			actionContent: "Terug naar Startscherm"
		}
	};
	constructor(appState, baasSession, appInfoManager, bootstrapOptions) {
		this.#appState = appState;
		this.#baasSession = baasSession;
		this.#appInfoManager = appInfoManager;
		this.#bootstrapOptions = bootstrapOptions;
	}
	checkOopsSupported() {
		return document.getElementById("oops") != null;
	}
	checkOopsVisible() {
		const el = document.getElementById("oops");
		return el != null && el.style.display === "block";
	}
	showOops(settings) {
		if (!settings) throw new Error("Oops settings are required.");
		if (!this.checkOopsSupported()) {
			console.warn("Oops DOM is not supported.");
			return;
		}
		const oopsElement = document.getElementById("oops");
		if (this.#appState?.location?.baasStatic?.url) {
			let image;
			const imgElement = document.getElementById("oops-image");
			if (settings.type === _OopsType.NotFound) image = "lv-melding-error-404.png";
			else image = `lv-melding-circle-0${generateRandomNumber(1, 8)}.png`;
			imgElement.src = concatUrl(this.#appState.location.baasStatic.url, "/images/all/", image);
		}
		oopsElement.style.display = "block";
		const appRoot = document.getElementById("app-root");
		if (appRoot) appRoot.parentElement.removeChild(appRoot);
		const loader = document.getElementById("app-loading");
		loader.style.display = "none";
		const config = this.#mappings[settings.type];
		const titleElement = document.getElementById("oops-title");
		if (titleElement) titleElement.innerText = config.title;
		const contentElement = document.getElementById("oops-content");
		if (contentElement) contentElement.innerText = config.content;
		const actionElement = document.getElementById("oops-action");
		if (actionElement) actionElement.addEventListener("click", config.action);
		const actionIconElement = document.getElementById("oops-action-icon");
		if (actionIconElement) actionIconElement.className = config.actionIcon;
		const actionContentElement = document.getElementById("oops-action-content");
		if (actionContentElement) actionContentElement.innerText = config.actionContent;
		const actionInfoElement = document.getElementById("oops-action-info");
		if (actionInfoElement) {
			actionInfoElement.style.display = config.actionInfo ? "block" : "none";
			actionInfoElement.innerText = config.actionInfo;
		}
	}
	async #oopsActionRefresh() {
		const appInfo = this.#appInfoManager.get();
		await this.#baasSession.reset();
		if (checkLocalhost() || this.#bootstrapOptions.public) {
			const appUrl = this.#appState?.location?.app?.url;
			if (appUrl) window.location.href = appUrl;
			else window.location.reload();
		} else {
			const redirectUrl = (this.#appState?.toepassing?.omgeving ?? appInfo?.ToepSecurityEnvironment) === "DMZETST" ? "https://alv-dotnet4-t/a/auth/web/bui/" : this.#appState?.config?.homeUrl ?? window.location.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.STARTSCHERM;
			window.location.href = redirectUrl;
		}
	}
	#oopsActionHome() {
		if (this.#appState?.config?.homeUrl) window.location = this.#appState.config.homeUrl;
		return Promise.resolve();
	}
};
_OopsDomService = __decorate([
	injectable(),
	__param(0, inject(BaasAppState)),
	__param(0, optional()),
	__param(1, inject(BaasSession)),
	__param(2, inject(_BarAppInfoManager)),
	__param(3, inject(BaasBootstrapOptions)),
	__metadata("design:paramtypes", [
		BaasAppState,
		BaasSession,
		_BarAppInfoManager,
		BaasBootstrapOptions
	])
], _OopsDomService);
let _BaasAuthFailedOrchestrator = class _BaasAuthFailedOrchestrator {
	#container;
	#oopsDomService;
	constructor(container, oopsDomService) {
		this.#container = container;
		this.#oopsDomService = oopsDomService;
	}
	async handle(context) {
		const customStrategy = this.#resolveCustomStrategy();
		if ((customStrategy ? await customStrategy.handle(context) : false) === false) this.#oopsDomService.showOops({ type: _OopsType.Authentication });
	}
	#resolveCustomStrategy() {
		return this.#container.isBound(BAAS_TYPES.BaasAuthFailedStrategy) ? this.#container.get(BAAS_TYPES.BaasAuthFailedStrategy) : void 0;
	}
};
_BaasAuthFailedOrchestrator = __decorate([
	injectable(),
	__param(0, inject(Container)),
	__param(1, inject(_OopsDomService)),
	__metadata("design:paramtypes", [Container, _OopsDomService])
], _BaasAuthFailedOrchestrator);
const _BAAS_TYPES = {
	BarGvkState: Symbol("BarGvkState"),
	BarHubMessageHandler: Symbol("BarHubMessageHandler"),
	BaasApm: Symbol("BaasApm")
};
let BarHubClient = class BarHubClient {
	#handlers;
	#connection;
	constructor(handlers) {
		this.retryCount = 1;
		this.#handlers = handlers;
	}
	init(options) {
		const neededHeaders = {
			"communication-app-code": options.appCode,
			"communication-user-token": options.securityTicketId
		};
		const connUrl = options?.hubUrl + "?connectionIdentifier=" + generateUuid();
		this.#connection = new signalR.HubConnectionBuilder().withUrl(connUrl, { headers: neededHeaders }).withAutomaticReconnect().configureLogging(options.logLevel == null ? signalR.LogLevel.None : options.logLevel).build();
		this.#connection.onclose(async () => {
			await this.#start({
				userId: options?.userId,
				appCode: options.appCode,
				securityTicketId: options.securityTicketId
			});
		});
		this.#connection.on("ReceiveMessage", (message) => {
			const handlers = this.#handlers.filter((h) => h.action === message.action);
			if (handlers.length) handlers.forEach((handler) => handler.handle(message.detail));
			else console.warn(`No handler found for hub messages of type ${message.action}.`, message);
		});
		this.#start({
			userId: options?.userId,
			appCode: options.appCode,
			securityTicketId: options.securityTicketId
		});
	}
	async #start(registration) {
		try {
			await this.#connection.start();
			console.info("Connected to Bar SignalR");
		} catch (e) {
			console.error(e);
			if (this.retryCount >= 5) console.error("Exceeded maximum retry times for connecting SignalR Hub (5).");
			else {
				this.retryCount++;
				setTimeout(() => this.#start(registration), 2e3);
			}
		}
	}
};
BarHubClient = __decorate([
	injectable(),
	__param(0, multiInject(_BAAS_TYPES.BarHubMessageHandler)),
	__metadata("design:paramtypes", [Array])
], BarHubClient);
let BaasI18n = class BaasI18n {
	constructor(options) {
		this.options = options;
		this.#init();
	}
	changeLanguage(lang) {
		return i18next.changeLanguage(lang);
	}
	addTranslations(namespace, translations, language = "nl") {
		const fullBundle = {};
		fullBundle[namespace] = translations;
		i18next.addResourceBundle(language, "LV", fullBundle, true, true);
	}
	get(key, params = {}) {
		if (!i18next.exists(key)) {
			console.warn(`Translation key "${key}" does not exist.`);
			return "";
		}
		return i18next.t(key, params);
	}
	#init() {
		const options = {
			lng: "nl",
			defaultNS: "LV",
			debug: false,
			resources: { nl: { LV: { INITIALIZED: "i18next has been initialized" } } }
		};
		if (this.options?.interpolation) options.interpolation = this.options.interpolation;
		i18next.init(options).then(() => {
			console.log("i18next has been initialized");
		}).catch((error) => {
			console.error("Failed to initialize i18next:", error);
		});
	}
};
BaasI18n = __decorate([injectable(), __metadata("design:paramtypes", [Object])], BaasI18n);
function baasI18n(key, params = {}) {
	if (!i18next.exists(key)) {
		console.warn(`Translation key "${key}" does not exist.`);
		return "";
	}
	return i18next.t(key, params);
}
const BAAS_SHARED_I18N = {
	STARTUP_ERROR: "Er heeft zich een fout voorgedaan bij het opstarten.",
	INIT_LOCAL_FAILED: "Local init has failed. The localhost web api server may not be running or a server-side error has occured.",
	PRE_INIT_LOCAL_FAILED: "Local pre-init has failed. The localhost web api server may not be running or a server-side error has occured.",
	XDC_ERROR: "Fout bij het synchroniseren (XDC).",
	XDC_SYNC_DISABLED: "XDC synchronisatie uitgeschakeld.",
	TARGET_ORIGIN_OVERRIDE_WARNING: "Target/release origin has been overridden by \"{{targetOrigin}}\". This can lead to undesired behavior.",
	AUTH_ERROR: "An error occurred while authenticating during the bootstrapping of the application."
};
let BaasMeetpuntService = class BaasMeetpuntService {
	get loggingEnabled() {
		const appinfo = this.appInfo.get();
		return this.state.config?.meetpunt.enabled && appinfo && (appinfo.ForcePerformanceLog || this.state.meetpuntenAutomatischAanmaken) && window.performance && window.performance.timing != null;
	}
	constructor(state, appInfo, apiClient) {
		this.state = state;
		this.appInfo = appInfo;
		this.apiClient = apiClient;
		this.queueProcessor = new BarQueueProcessor(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "meetpuntQueue");
	}
	log(meetpunt) {
		if (!this.loggingEnabled) return;
		if (meetpunt.appCode == null) meetpunt.appCode = this.state.toepassing.code;
		if (meetpunt.meetpuntType == null) meetpunt.meetpuntType = _BAAS_SHARED_CONSTANTS.DEFAULT_MEETPUNT_TYPE;
		this.queueProcessor.enqueue(meetpunt);
	}
	async start() {
		this.queueProcessor.onExecute = async (meetpunten) => {
			await this.apiClient.save(meetpunten);
		};
		this.queueProcessor.start(this.state.config.meetpunt.interval, this.state.config.meetpunt.itemCount);
	}
};
BaasMeetpuntService = __decorate([
	injectable(),
	__param(2, inject(BAAS_TYPES.BaasMeetpuntApiClient)),
	__metadata("design:paramtypes", [
		BaasAppState,
		_BarAppInfoManager,
		Object
	])
], BaasMeetpuntService);
var BarRapSegment;
(function(BarRapSegment) {
	BarRapSegment["Applicative"] = "a";
	BarRapSegment["ReverseProxy"] = "r";
	BarRapSegment["Public"] = "p";
})(BarRapSegment || (BarRapSegment = {}));
let BarRapUrlHelper = class BarRapUrlHelper {
	get baasStaticSegment() {
		return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAAS_STATIC.replace("{segment}", this.rapSegment);
	}
	get baasWebSegment() {
		return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAAS_WEB.replace("{segment}", this.rapSegment);
	}
	get idpOidcWebSegment() {
		return _BAAS_SHARED_CONSTANTS.URL_SEGMENT.IDP_OIDC.replace("{segment}", this.rapSegment);
	}
	get rapSegment() {
		return this.config.public ? BarRapSegment.Public : BarRapSegment.Applicative;
	}
	constructor(config) {
		this.config = config;
	}
	parseUrl(url) {
		if (this.config.public && url.toLowerCase().indexOf(`/${BarRapSegment.Applicative}/`) > -1) return url.toLowerCase().replace(`/${BarRapSegment.Applicative}/`, `/${BarRapSegment.Public}/`);
		return url;
	}
};
BarRapUrlHelper = __decorate([injectable(), __metadata("design:paramtypes", [BaasBootstrapOptions])], BarRapUrlHelper);
let BaasBrowserCapabilities = class BaasBrowserCapabilities {
	get storageAccess() {
		return getStorageAccess();
	}
	get notifications() {
		const supported = typeof Notification !== "undefined";
		return {
			supported,
			permission: supported ? Notification.permission : void 0
		};
	}
};
BaasBrowserCapabilities = __decorate([injectable()], BaasBrowserCapabilities);
let BarNotificationManager = class BarNotificationManager {
	constructor(browserCapabilities) {
		this.browserCapabilities = browserCapabilities;
	}
	async init() {
		if (!this.browserCapabilities.notifications.supported) {
			console.warn("Notification API is not supported on this device.");
			return;
		}
		const status = await Notification.requestPermission();
		console.info("Notification permission status:", status);
	}
	async show(notification) {
		if (!this.browserCapabilities.notifications.supported) {
			console.warn("Notification API is not supported on this device.");
			return;
		}
		if (this.browserCapabilities.notifications.permission === "granted") {
			const reg = await navigator.serviceWorker.getRegistration();
			if (reg) await reg.showNotification(notification.title, notification.options);
			else console.warn("No service worker registration retrieved to show notification.");
		}
	}
};
BarNotificationManager = __decorate([
	injectable(),
	__param(0, inject(BaasBrowserCapabilities)),
	__metadata("design:paramtypes", [BaasBrowserCapabilities])
], BarNotificationManager);
const INACTIVE_MESSAGE = "APM is not active.";
let _BaasApm = class _BaasApm {
	#alreadyWarnedInactive = false;
	constructor(bootstrapOptions, state) {
		this.bootstrapOptions = bootstrapOptions;
		this.state = state;
	}
	get active() {
		const serverAllows = this.#serverConfig.rumActive === true;
		switch (this.#clientConfig.enabled) {
			case "force-on": return true;
			case "force-off": return false;
			case "allow-local": return serverAllows;
			default:
				if (checkLocalhost()) return false;
				return serverAllows;
		}
	}
	get #apm() {
		if (!this.apm) throw new Error("APM is not initialized");
		return this.apm;
	}
	get #clientConfig() {
		return this.bootstrapOptions.telemetry || {};
	}
	get #serverConfig() {
		return this.state.config?.baasStatic?.telemetry || {};
	}
	init() {
		if (this.active) {
			let distributedTracing = this.#serverConfig.distributedTracing;
			if (distributedTracing === void 0) distributedTracing = false;
			const ignoreTransactions = this.#parseIgnorePatterns(this.#serverConfig.ignoreTransactions);
			const logLevel = this.#serverConfig.logLevel ?? (this.state.toepassing.securityLevel === BaasSecurityLevel.Production ? "warn" : "debug");
			this.apm = init({
				serverUrl: this.#serverConfig.rumServerUrl,
				serviceName: this.state.toepassing.code,
				serviceVersion: this.state.toepassing.versie,
				environment: this.state.toepassing.omgeving,
				logLevel,
				distributedTracing,
				pageLoadTransactionName: window.location.pathname,
				transactionSampleRate: this.#serverConfig.transactionSampleRate ?? 1,
				ignoreTransactions
			});
			const ignoreUrls = this.#parseIgnorePatterns(this.#serverConfig.ignoreUrls);
			if (ignoreUrls?.length) this.apm.addFilter((payload) => {
				const shouldIgnore = (url) => ignoreUrls.some((pattern) => pattern instanceof RegExp ? pattern.test(url) : url.toLowerCase() === pattern.toLowerCase());
				const filterSpans = (spans) => spans.filter((span) => {
					const url = span.context?.http?.url;
					return url ? !shouldIgnore(url) : true;
				});
				if (payload.spans) payload.spans = filterSpans(payload.spans);
				if (payload.transactions) {
					for (const t of payload.transactions) if (t.spans) t.spans = filterSpans(t.spans);
				}
				return payload;
			});
			let lastAnchorHref = null;
			document.addEventListener("click", (event) => {
				lastAnchorHref = (event.target?.closest("a"))?.getAttribute("href") ?? null;
			}, { capture: true });
			this.apm.observe("transaction:end", (tr) => {
				switch (tr.type) {
					case "page-load":
						tr.name = window.location.pathname;
						break;
					case "user-interaction": {
						const classes = tr.context?.custom?.classes;
						if (classes) tr.name += `.${classes.replace(" ", ".")}`;
						break;
					}
					case "page-exit":
						if (lastAnchorHref) tr.addContext({ custom: { href: lastAnchorHref } });
						lastAnchorHref = null;
				}
			});
			if (this.state.basiscontext) {
				this.apm.setUserContext({
					username: this.state.basiscontext.fullUsername,
					id: this.state.basiscontext.userId
				});
				if (this.state.basiscontext.voorwie) this.apm.addLabels({ uservoorwie: `${this.state.basiscontext.voorwie};${this.state.basiscontext.voorwieRolcode}` });
				if (this.state.basiscontext.hoedanigheid) this.apm.addLabels({ userhoed: `${this.state.basiscontext.hoedanigheid};${this.state.basiscontext.hoedanigheidRolcode}` });
			}
			return true;
		}
		this.#notActiveWarning();
		return false;
	}
	startTransaction(name, type) {
		if (!this.active) throw new Error(INACTIVE_MESSAGE);
		return this.#apm.startTransaction(name, type);
	}
	handleError(error) {
		if (this.active) this.#apm.captureError(error.originalError || error);
		else this.#notActiveWarning();
	}
	#notActiveWarning() {
		if (!this.#alreadyWarnedInactive) {
			console.warn(INACTIVE_MESSAGE);
			this.#alreadyWarnedInactive = true;
		}
	}
	#parseIgnorePatterns(value) {
		if (!value) return void 0;
		return value.map((v) => v.trim()).filter(Boolean).map((v) => {
			if (v.startsWith("regex:")) return new RegExp(v.slice(6), "i");
			if (v.startsWith("exact:")) return v.slice(6);
			return v;
		});
	}
};
_BaasApm = __decorate([injectable(), __metadata("design:paramtypes", [BaasBootstrapOptions, BaasAppState])], _BaasApm);
var BaasConnectivity;
(function(BaasConnectivity) {
	BaasConnectivity["Online"] = "Online";
	BaasConnectivity["Offline"] = "Offline";
})(BaasConnectivity || (BaasConnectivity = {}));
const STORAGE_KEY_USER_INFO_PREFIX = _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "userInfo_";
let BaasBootstrap = class BaasBootstrap {
	#stopwatch;
	#outcome;
	#targetOrigin;
	#configHandled = false;
	get #mustAuthenticate() {
		return this.options.auth != null ? this.options.auth.enabled : false;
	}
	get #authFailedUrl() {
		return this.state.config.auth.failedUrl;
	}
	get #targetOriginOverride() {
		return window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "targetOrigin");
	}
	set #targetOriginOverride(value) {
		if (value == null) window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "targetOrigin");
		else window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "targetOrigin", value);
	}
	constructor(state, options, idpClient, logOff, i18n, cacheManager, baasContextService, globalStore, rapUrlHelper, auth, appInfo, audit, commonApiClient, securityApiClient, _BaasStaticConfigurationApiClient, meetpunt, logging, hubClient, notificationManager, oopsDomService, apm, telemetry, oidcAuthService) {
		this.state = state;
		this.options = options;
		this.idpClient = idpClient;
		this.logOff = logOff;
		this.i18n = i18n;
		this.cacheManager = cacheManager;
		this.baasContextService = baasContextService;
		this.globalStore = globalStore;
		this.rapUrlHelper = rapUrlHelper;
		this.auth = auth;
		this.appInfo = appInfo;
		this.audit = audit;
		this.commonApiClient = commonApiClient;
		this.securityApiClient = securityApiClient;
		this._BaasStaticConfigurationApiClient = _BaasStaticConfigurationApiClient;
		this.meetpunt = meetpunt;
		this.logging = logging;
		this.hubClient = hubClient;
		this.notificationManager = notificationManager;
		this.oopsDomService = oopsDomService;
		this.apm = apm;
		this.telemetry = telemetry;
		this.oidcAuthService = oidcAuthService;
	}
	#startTime;
	async bootstrap() {
		this.#startTime = performance.now();
		this.#outcome = void 0;
		if (this.options.auth.contextTransportModus === BaasContextTransportModus.Cookie) document.addEventListener("visibilitychange", (ev) => {
			this.appInfo.persistBasiscontext();
		});
		this.i18n.addTranslations("DOMAIN_BAR_SHARED", BAAS_SHARED_I18N);
		this.#configHandled = false;
		this.logOff.requestLogOff = void 0;
		await this.#launchLifeCycle();
		return this.#outcome;
	}
	async #launchLifeCycle() {
		this.#defineLogging();
		await requestStorageAccess();
		this.idpClient.options.targetUrl = window.location.href;
		const pageIdKey = "pageId";
		this.state[pageIdKey] = generateUuid();
		const connectivityKey = "connectivity";
		this.state[connectivityKey] = this.options.connectivity || BaasConnectivity.Online;
		const locationKey = "location";
		this.state[locationKey] = {};
		const appRootKey = "appRoot";
		this.state.location[appRootKey] = extractUrlParts(this.#defineAppRootUrl());
		const appKey = "app";
		this.state.location[appKey] = extractUrlParts(this.#defineAppUrl());
		const apiRootKey = "apiRoot";
		this.state.location[apiRootKey] = extractUrlParts(this.#defineApiRootUrl());
		if (this.options.localhost) {
			const core = ensureGlobal("lvflanders.bar.shared");
			Object.defineProperty(core, "targetOrigin", {
				get: () => {
					return this.#targetOriginOverride;
				},
				set: (value) => {
					this.#targetOriginOverride = value;
				},
				configurable: true
			});
			this.#targetOrigin = this.#targetOriginOverride;
			if (this.#targetOrigin == null) this.#targetOrigin = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "targetOrigin");
			else {
				const msg = this.i18n.get("DOMAIN_BAR_SHARED.PRE_INIT_LOCAL_FAILED", { targetOrigin: this.#targetOrigin });
				console.warn(msg);
			}
			if (!this.#targetOrigin) try {
				const data = await this.commonApiClient.preInitLocal();
				this.#targetOrigin = data.targetOrigin || data.TargetOrigin;
				if (this.#targetOrigin != null) window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "targetOrigin", this.#targetOrigin);
			} catch {
				const msg = this.i18n.get("DOMAIN_BAR_SHARED.PRE_INIT_LOCAL_FAILED");
				console.warn(msg);
			}
		}
		const releaseKey = "release";
		this.state.location[releaseKey] = extractUrlParts(this.#defineReleaseOrigin());
		const barWebKey = "barWeb";
		this.state.location[barWebKey] = extractUrlParts(this.#defineBarWebRootUrl());
		const baasStaticKey = "baasStatic";
		this.state.location[baasStaticKey] = extractUrlParts(this.#defineBaasStaticRootUrl());
		const baasWebKey = "baasWeb";
		this.state.location[baasWebKey] = extractUrlParts(this.#defineBaasWebRootUrl());
		this.idpClient.options.wwUrl = this.state.location.baasStatic.url;
		this.#defineDefaultIdpRootUrl();
		if (!this.auth.authenticated) await this.#launchPreInit();
		else if (this.auth.authenticating && this.#mustAuthenticate) await this.#launchAuth(true);
		else await this.#launchInit(true);
	}
	#getInitRequest(loadConfig) {
		return {
			appCode: this.options.appCode,
			appVersion: this.options.appVersion,
			loadConfig,
			loadToepassing: false,
			meetpuntType: _BAAS_SHARED_CONSTANTS.DEFAULT_MEETPUNT_TYPE
		};
	}
	async #launchAuth(preInitSkipped) {
		try {
			const authSuccess = await this.auth.authenticate();
			if (authSuccess === true) await this.#launchInit(preInitSkipped);
			else if (authSuccess === false) {
				window.location.href = this.#authFailedUrl;
				this.#handleAuthError();
				this.#outcome = BaasBootstrapOutcome.AuthFailed;
			} else if (authSuccess === void 0 || authSuccess === null) this.#outcome = BaasBootstrapOutcome.Authenticating;
		} catch (error) {
			console.error(error);
			window.location.href = this.#authFailedUrl;
			this.#handleAuthError(error);
		}
	}
	async #launchPreInit() {
		const loadConfig = this.#tryLoadConfigFromStorage();
		const initRequest = this.#getInitRequest(loadConfig);
		try {
			const data = await this.commonApiClient.preInit(initRequest);
			this.#handleConfig(data.config, true);
			this.#handleToepassing(data.toepassing);
			await this.#defineBaasStaticConfiguration();
			if (this.#mustAuthenticate && !this.auth.authenticated) await this.#launchAuth(false);
			else await this.#launchInit(false);
		} catch (error) {
			this.#handleStartupError(error);
		}
	}
	#tryLoadConfigFromStorage() {
		let notLoaded = true;
		const configStr = window.sessionStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "config");
		if (configStr != null && configStr.length > 0) {
			notLoaded = false;
			const config = JSON.parse(configStr);
			this.#handleConfig(config, false);
		}
		return notLoaded;
	}
	#handleConfig(config, putInStorage) {
		if (this.#configHandled) return;
		const configKey = "config";
		this.state[configKey] = config;
		if (putInStorage) window.sessionStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX + "config", JSON.stringify(config));
		const processIdpRootUrl = (origin) => {
			let idpRootUrl = origin;
			if (!config.idpRootUrl.startsWith("/")) idpRootUrl += "/";
			idpRootUrl += config.idpRootUrl;
			config.idpRootUrl = idpRootUrl;
			config.idpRootUrl = this.rapUrlHelper.parseUrl(config.idpRootUrl);
		};
		if (this.options.localhost) {
			if (this.options._solution && !this.options.auth.forceRemote) {
				config.auth.url = "https://localhost:44307/auth?noredirect=1";
				config.auth.failedUrl = "https://localhost:44307/authenticate-failed";
				config.logoutUrl = "https://localhost:44307/auth/logout";
				config.errorUrl = "https://alv-dotnet4-d/startscherm/domain.startscherm.Web/probleemrapport/";
				config.idpRootUrl = "https://alv-dotnet4-d/a/idp/wui";
			} else processIdpRootUrl(this.state.location.release.origin);
		} else processIdpRootUrl(window.location.origin);
		if (config.idpRootUrl) this.idpClient.options.idpUrl = config.idpRootUrl;
		this.#configHandled = true;
	}
	#defineLogging() {
		const log = ensureGlobal("lvflanders.bar.shared.log");
		log.enabled = {};
		Object.defineProperty(log.enabled, "cacheStorage", {
			get() {
				const enabledStr = window.localStorage.getItem("lvflanders.bar.shared.log.enabled.cacheStorage");
				return enabledStr != null ? JSON.parse(enabledStr) : false;
			},
			set(newValue) {
				window.localStorage.setItem("lvflanders.bar.shared.log.enabled.cacheStorage", JSON.stringify(newValue));
			},
			configurable: true
		});
		Object.defineProperty(log, "format", {
			get() {
				return window.localStorage.getItem("lvflanders.bar.shared.log.format") || "object";
			},
			set(newValue) {
				if (newValue) newValue = newValue.toLowerCase();
				if (!newValue || newValue === "json" || newValue === "object") window.localStorage.setItem("lvflanders.bar.shared.log.format", newValue);
				else console.warn("can only set value to 'json', 'object' or nothing.");
			},
			configurable: true
		});
	}
	#defineAppRootUrl() {
		let url;
		if (this.options.appRootUrl) url = this.options.appRootUrl;
		else if (this.options.appUrl) url = this.options.appUrl;
		else {
			const baseTag = document.getElementsByTagName("base");
			if (baseTag.length && baseTag[0].href) url = baseTag[0].href;
			else url = location.origin;
		}
		if (!checkUrlAbsolute(url)) {
			if (!url.startsWith("/")) url = "/" + url;
			url = location.origin + url;
		}
		if (!url.endsWith("/")) url += "/";
		return url.toLowerCase();
	}
	#defineApiRootUrl() {
		if (this.options.apiRootUrl) return this.options.apiRootUrl;
		return this.#defineAppRootUrl();
	}
	#defineAppUrl() {
		if (this.options.appUrl) {
			if (checkUrlAbsolute(this.options.appUrl)) return this.options.appUrl;
			const appUrl = this.options.appUrl.startsWith("/") ? this.options.appUrl : `/${this.options.appUrl}`;
			return `${this.state.location.appRoot.origin}${appUrl}`;
		}
		return this.state.location.appRoot.url;
	}
	#handleStartupError(err) {
		console.error(err);
		if (this.oopsDomService.checkOopsVisible()) return;
		this.oopsDomService.showOops({ type: _OopsType.Technical });
	}
	#handleAuthError(error = void 0) {
		if (error) console.error(error);
		throw new Error(this.i18n.get("DOMAIN_BAR_SHARED.AUTH_ERROR"));
	}
	async #defineBrowserSession() {
		let cookieSessionId = this.appInfo.sessionId;
		if (!cookieSessionId) {
			cookieSessionId = generateUuid();
			this.appInfo.sessionId = cookieSessionId;
		}
		let sessionId = await this.globalStore.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.SESSION_ID);
		const proceed = async (clearSession) => {
			const sessionIdKey = "sessionId";
			this.state[sessionIdKey] = sessionId;
			if (!this.options.cacheStorage?.disableInvalidation) await this.cacheManager.invalidate(clearSession);
			if (clearSession) this.#clearTempState();
		};
		if (sessionId !== cookieSessionId) {
			sessionId = cookieSessionId;
			await this.globalStore.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.SESSION_ID, sessionId, { session: false });
			await proceed(true);
		} else await proceed(false);
	}
	#tryRunIdpValidation() {
		if (this.#mustAuthenticate && this.options.auth.validation !== false && this.options.auth.contextTransportModus === BaasContextTransportModus.Cookie) this.idpClient.validate();
	}
	async #launchInit(preInitSkipped) {
		if (!this.auth.authenticated && this.options._noInitPaths?.some((noInitPath) => {
			const fullPath = concatUrl(this.state.location.app.pathname, noInitPath).toLowerCase();
			return window.location.pathname.toLowerCase() === fullPath;
		})) {
			await this.#initializeFinal(void 0, preInitSkipped);
			return;
		}
		const loadConfig = !this.#configHandled && this.#tryLoadConfigFromStorage();
		const initRequest = this.#getInitRequest(loadConfig);
		initRequest.loadToepassing = this.state.toepassing == null;
		try {
			const data = await this.commonApiClient.init(initRequest);
			if (data.config != null) this.#handleConfig(data.config, true);
			const baasVersionKey = "baasVersion";
			this.state[baasVersionKey] = data.baasVersion;
			const requestTokenKey = "requestToken";
			this.state[requestTokenKey] = data.requestToken;
			if (data.toepassing != null) this.#handleToepassing(data.toepassing);
			if (this.options.auth.contextTransportModus === BaasContextTransportModus.Header && data?.state !== null && typeof data.state !== "undefined") {
				const currentContext = this.baasContextService.getBaasContextFromStorage();
				const updatedContext = {
					...data.state,
					userTicketGuid: currentContext.userTicketGuid
				};
				this.baasContextService.saveBaasContextToStorage(updatedContext);
			}
			const meetpuntenAutomatischAanmakenKey = "meetpuntenAutomatischAanmaken";
			this.state[meetpuntenAutomatischAanmakenKey] = data.meetpuntenAutomatischAanmaken == null ? false : data.meetpuntenAutomatischAanmaken;
			if (this.options.localhost) try {
				const localData = await this.commonApiClient.initLocal();
				const localRequestTokenKey = "localRequestToken";
				this.state[localRequestTokenKey] = localData.requestToken;
			} catch (error) {
				const msg = this.i18n.get("DOMAIN_BAR_SHARED.INIT_LOCAL_FAILED");
				console.error(msg);
				this.#handleStartupError(error);
			}
			await this.#initializeFinal(data, preInitSkipped);
		} catch (error) {
			this.#handleStartupError(error);
		}
	}
	#handleToepassing(toepassing) {
		if (this.options.appVersion) {
			const versieKey = "versie";
			toepassing[versieKey] = this.options.appVersion;
		}
		const toepassingKey = "toepassing";
		this.state[toepassingKey] = toepassing;
		this.idpClient.options.zone = this.state.toepassing.zone;
		if (!this.options.auth.withActingScopeHeaders && (this.options.auth.syncBasiscontextAfterRequestCompletion == null || this.options.auth.syncBasiscontextAfterRequestCompletion === true) && this.state.toepassing.securityLevel !== BaasSecurityLevel.Production) {
			this.options.auth.syncBasiscontextAfterRequestCompletion = false;
			this.options.auth.withActingScopeHeaders = true;
		}
	}
	async #initializeFinal(data, preInitSkipped) {
		await this.#defineBrowserSession();
		const audit = {
			auditId: this.state.pageId,
			parentAuditId: this.state.sessionId,
			auditType: BaasAuditType.Page,
			appCode: this.options.appCode,
			details: {
				URL: document.location.href,
				"APP-VERSION": this.options.appVersion
			}
		};
		this.audit.log(audit);
		_addAuditInfoOnDocument(audit);
		this.appInfo.init(data);
		const basiscontext = this.appInfo.getBasiscontext();
		const basiscontextKey = "basiscontext";
		this.state[basiscontextKey] = basiscontext;
		if (this.auth.postProcessing) await this.auth.postProcessing();
		if (preInitSkipped) await this.#defineBaasStaticConfiguration();
		this.apm.init();
		await this.#initLvOidc();
		if (await this.#applyPreRoutingInterceptions()) {
			this.logOff.startListening();
			this.#tryRunIdpValidation();
			await this.#defineAuditMeetpunten();
			this.#auditDependencies();
			this.#defineHubClient();
			await this.#defineUserInfo();
			await this.#defineNotifications();
			this.#outcome = BaasBootstrapOutcome.Initialized;
		} else this.#outcome = BaasBootstrapOutcome.Halted;
		if (this.telemetry.currentTransaction) this.telemetry.currentTransaction.startSpan("Bootstrap BaaS App", "bootstrap-baas-app", { startTime: this.#startTime }).end();
	}
	#defineReleaseOrigin() {
		let result;
		if (this.#targetOrigin != null) result = this.#targetOrigin;
		else {
			let src = null;
			for (const script of Array.from(document.scripts)) if (script.src != null && script.src.indexOf("lvflanders-bar-ng") > -1) {
				src = script.src;
				break;
			}
			if (src != null) result = extractUrlOrigin(src);
			else result = window.location.origin;
		}
		return result;
	}
	#defineBarWebRootUrl() {
		let url;
		if (this.options.connectivity === BaasConnectivity.Offline) url = this.state.location.appRoot.url + "domain.basisarchitectuur.web/";
		else if (this.options.localhost) url = this.state.location.release.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAR_WEB;
		else url = location.origin + _BAAS_SHARED_CONSTANTS.URL_SEGMENT.BAR_WEB;
		return url;
	}
	#defineBaasWebRootUrl() {
		return concatUrl(this.options.localhost ? this.state.location.release.origin : location.origin, this.rapUrlHelper.baasWebSegment);
	}
	#defineDefaultIdpRootUrl() {
		if (this.idpClient.options.idpUrl) return;
		let origin;
		if (this.state.location?.release?.origin) origin = this.state.location.release.origin;
		else if (checkLocalhost()) origin = "https://alv-dotnet4-d";
		else origin = window.location.origin;
		this.idpClient.options.idpUrl = concatUrl(origin, `/${this.rapUrlHelper.rapSegment}/idp/wui`);
	}
	#defineBaasStaticRootUrl() {
		let url;
		if (this.options.connectivity === BaasConnectivity.Offline) url = this.state.location.appRoot.url + "domain.baas-static.web/";
		else if (this.options.localhost) {
			if (this.options._solution && this.options.staticRootUrl) {
				url = this.options.staticRootUrl;
				if (!url.endsWith("/")) url += "/";
			} else url = this.state.location.release.origin + this.rapUrlHelper.baasStaticSegment;
		} else url = location.origin + this.rapUrlHelper.baasStaticSegment;
		return url;
	}
	async #defineBaasStaticConfiguration() {
		if (this.state.connectivity === BaasConnectivity.Online) {
			const config = await this._BaasStaticConfigurationApiClient.getConfiguration();
			this.#handleBaasStaticConfiguration(config);
		}
	}
	#handleBaasStaticConfiguration(config) {
		config?.appMenuItems?.forEach((ami) => {
			ami.url = this.#processAppMenuUrl(ami.url);
			if (this.options.localhost) ami.urlLocalhost = this.#processAppMenuUrl(ami.urlLocalhost);
		});
		const configKey = "config";
		this.state[configKey].baasStatic = config;
		if (this.options.public === true && config.public?.homeUrl) this.state.config.homeUrl = config.public.homeUrl;
	}
	#processAppMenuUrl(url) {
		if (!url || checkUrlAbsolute(url)) return url;
		return `${this.state.location.release.origin}${url}`;
	}
	async #defineAuditMeetpunten() {
		const perflog = parseQueryStringValue("perflog");
		if (perflog != null) {
			const appinfo = this.appInfo.get();
			if (appinfo != null) switch (perflog.toLowerCase()) {
				case "off":
					appinfo.ForcePerformanceLog = false;
					await this.appInfo.save(appinfo);
					break;
				case "on":
					appinfo.ForcePerformanceLog = true;
					await this.appInfo.save(appinfo);
			}
		}
		if (this.meetpunt.loggingEnabled) {
			const pageLoadTime = window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart;
			this.meetpunt.log({
				meetpunt: "DOM_CONTENT_LOADED_END",
				meetpuntDetail: document.location.pathname,
				waarde: pageLoadTime,
				meetpuntInfo: document.location.href,
				auditId: this.state.pageId
			});
			this.#stopwatch = new BarStopwatch();
			this.#stopwatch.start();
		}
		if (this.state.config?.audit?.enabled == true) this.audit.start();
		if (this.meetpunt.loggingEnabled) await this.meetpunt.start();
		this.logging.init();
		if (this.state.config?.log?.enabled == true) await this.logging.start();
	}
	#auditDependencies() {
		const scripts = [];
		const scriptTags = document.getElementsByTagName("script");
		for (const scriptTag of Array.from(scriptTags)) if (scriptTag.src != null && scriptTag.src.length > 0) {
			const script = {
				src: scriptTag.src,
				type: scriptTag.type
			};
			scripts.push(script);
		}
		const links = [];
		const linkTags = document.getElementsByTagName("link");
		for (const linkTag of Array.from(linkTags)) if (linkTag.href != null && linkTag.href.length > 0) {
			const link = {
				href: linkTag.href,
				rel: linkTag.rel,
				type: linkTag.type
			};
			links.push(link);
		}
		this.audit.log({
			parentAuditId: this.state.pageId,
			auditType: BaasAuditType.Dependencies,
			appCode: this.options.appCode,
			details: {
				SCRIPTS: JSON.stringify(scripts),
				LINKS: JSON.stringify(links)
			}
		});
	}
	#defineHubClient() {
		if (this.options.hub?.enabled) {
			const hubUrl = concatUrl(this.state.location.baasWeb.url, "/baas/hubs/bui");
			this.hubClient.init({
				hubUrl,
				userId: this.state.basiscontext?.userId,
				appCode: this.state.toepassing.code,
				securityTicketId: this.state.basiscontext?.securityTicketId
			});
		}
	}
	async #defineUserInfo() {
		let userData;
		if (!this.auth.authenticated) userData = { hoedanigheidInfoSet: [] };
		else {
			const cacheKey = STORAGE_KEY_USER_INFO_PREFIX + (this.state.basiscontext?.userId || "UNDEFINED");
			let cachedItem = window.sessionStorage.getItem(cacheKey);
			if (cachedItem) userData = JSON.parse(cachedItem);
			else {
				userData = await this.securityApiClient.getUserInfo();
				cachedItem = JSON.stringify(userData);
				window.sessionStorage.setItem(cacheKey, cachedItem);
			}
		}
		const userInfoKey = "userInfo";
		this.state[userInfoKey] = userData;
	}
	async #defineNotifications() {
		if (this.options.notifications === true) await this.notificationManager.init();
	}
	async #applyPreRoutingInterceptions() {
		if (this.#checkRouteMatch(_BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.SPOTFIRE)) {
			while (document.body.firstChild) document.body.removeChild(document.body.firstChild);
			if (typeof spotfire !== "undefined") spotfire.webPlayer.oauthEnd();
			else console.error("Spotfire CDN JS api not loaded.");
			this.#clearDocument("Spotfire callback");
			return false;
		}
		if (this.options.auth?._experimentalOidcEnabled) {
			if (!this.#checkRouteMatch(_BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.LV)) {
				if (await this.oidcAuthService.getUser()) await this.oidcAuthService.renewToken();
			} else {
				await this.oidcAuthService.callback();
				return false;
			}
		}
		return true;
	}
	async #initLvOidc() {
		if (!this.options.auth?._experimentalOidcEnabled) return;
		const options = {
			authority: concatUrl(this.state.location.release.url, this.rapUrlHelper.idpOidcWebSegment),
			clientId: "oidc_lv_public_test1",
			callbackUri: concatUrl(checkLocalhost() ? this.state.location.app.url : concatUrl(this.state.location.baasStatic.url, "/bui"), _BAAS_SHARED_CONSTANTS.AUTH.OIDC.CALLBACK.LV),
			clientScope: "openid offline_access",
			userAddedCallback: (user) => {}
		};
		this.oidcAuthService.init(options);
	}
	#checkRouteMatch(path) {
		return new URL(window.location.href).pathname.split("?")[0].endsWith(path);
	}
	#clearDocument(comment) {
		const htmlElement = document.documentElement;
		while (htmlElement.attributes.length > 0) htmlElement.removeAttribute(htmlElement.attributes[0].name);
		const commentElement = document.createComment(comment);
		document.body.appendChild(commentElement);
	}
	#clearTempState() {
		clearFromStorage(localStorage, (key) => key.startsWith(`${_BAAS_SHARED_CONSTANTS.TEMP_STORAGE_STATE_KEY}.`));
	}
};
BaasBootstrap = __decorate([
	injectable(),
	__param(7, inject(BarCacheStore)),
	__param(7, tagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global)),
	__param(12, inject(BAAS_TYPES.BaasCommonApiClient)),
	__param(13, inject(BAAS_TYPES.BaasSecurityApiClient)),
	__param(21, inject(BAAS_TYPES.BaasTelemetry)),
	__param(22, inject(BAAS_TYPES._BaasLvOicdAuthService)),
	__metadata("design:paramtypes", [
		BaasAppState,
		BaasBootstrapOptions,
		BarIdpClient,
		_BaasLogOffUtility,
		BaasI18n,
		BarCacheManager,
		_BaasContextManager,
		BarCacheStore,
		BarRapUrlHelper,
		BaasAuthService,
		_BarAppInfoManager,
		BaasAuditService,
		Object,
		Object,
		_BaasStaticConfigurationApiClient,
		BaasMeetpuntService,
		BaasLoggingService,
		BarHubClient,
		BarNotificationManager,
		_OopsDomService,
		_BaasApm,
		Object,
		BaasOidcAuthService
	])
], BaasBootstrap);
const baasContext = { container: void 0 };
let BaasAuditDefaultApiClient = class BaasAuditDefaultApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async save(auditItems) {
		await this.axios.post(this.state.location.apiRoot.url + "baas/audit/bui/messages", auditItems);
	}
};
BaasAuditDefaultApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BaasAuditDefaultApiClient);
let BaasCommonDefaultApiClient = class BaasCommonDefaultApiClient {
	constructor(state, axiosBaas, axiosPlain) {
		this.state = state;
		this.axiosBaas = axiosBaas;
		this.axiosPlain = axiosPlain;
	}
	async preInitLocal() {
		return (await this.axiosPlain.post(this.state.location.apiRoot.url + "baas/bootloader/pre-init-local", null, { withCredentials: true })).data;
	}
	async preInit(request) {
		const response = await this.axiosPlain.post(this.state.location.apiRoot.url + "baas/bootloader/pre-init", request, { withCredentials: true });
		if (response.data?.toepassing) this.parseToepassing(response.data.toepassing);
		return response.data;
	}
	async init(request) {
		const response = await this.axiosBaas.post(this.state.location.apiRoot.url + "baas/bootloader/init", request);
		if (response.data?.toepassing) this.parseToepassing(response.data.toepassing);
		return response.data;
	}
	async initLocal() {
		return (await this.axiosBaas.post(this.state.location.apiRoot.url + "baas/bootloader/init-local", {})).data;
	}
	async saveEndpoints(appCode, appVersion, endpoints) {
		const url = `${this.state.location.apiRoot.url}baas/bootloader/endpoints/appcode/${appCode}/version/${appVersion}`;
		try {
			const response = await this.axiosBaas.post(url, endpoints);
			if (!response.data?.returnVal) console.error("An error occurred while saving the endpoints", response.data);
		} catch (err) {
			console.error("An error occurred while saving the endpoints", err);
		}
	}
	parseToepassing(toepassing) {
		if (typeof toepassing.securityLevel === "string") {
			const securityLevelKey = "securityLevel";
			toepassing[securityLevelKey] = this.parseSecLevelFromString(toepassing.securityLevel);
		}
	}
	parseSecLevelFromString(securityLevel) {
		if (securityLevel == null) return null;
		switch (securityLevel.toUpperCase()) {
			case "DEV": return BaasSecurityLevel.Development;
			case "TST": return BaasSecurityLevel.Test;
			case "PRD": return BaasSecurityLevel.Production;
			default: return BaasSecurityLevel.Unknown;
		}
	}
};
BaasCommonDefaultApiClient = __decorate([
	injectable(),
	__param(1, inject(BAAS_TYPES.AxiosInstance)),
	__param(1, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__param(2, inject(BAAS_TYPES.AxiosInstance)),
	__param(2, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
	__metadata("design:paramtypes", [
		BaasAppState,
		Function,
		Function
	])
], BaasCommonDefaultApiClient);
let BaasConfigurationDefaultApiClient = class BaasConfigurationDefaultApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async read(model) {
		const url = `${this.state.location.apiRoot.url}baas/configuration/value/appCode/${model.appCode}/path/${encodeURI(model.key)}`;
		return (await this.axios.get(url)).data;
	}
};
BaasConfigurationDefaultApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BaasConfigurationDefaultApiClient);
let BaasLoggingDefaultApiClient = class BaasLoggingDefaultApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	save(logs) {
		return this.axios.post(this.state.location.apiRoot.url + "baas/logging/bui/messages", logs);
	}
};
BaasLoggingDefaultApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BaasLoggingDefaultApiClient);
let BaasMeetpuntDefaultApiClient = class BaasMeetpuntDefaultApiClient {
	constructor() {
		this.warnedNotAvailable = false;
	}
	save(meetpunten) {
		this.warnNotAvailable();
		return Promise.resolve();
	}
	warnNotAvailable() {
		if (!this.warnedNotAvailable) {
			this.warnedNotAvailable = true;
			console.warn("Meetpunten zijn (nog) niet beschikbaar binnen BaaS.Net...");
		}
	}
};
BaasMeetpuntDefaultApiClient = __decorate([injectable()], BaasMeetpuntDefaultApiClient);
let BaasMeldingDefaultApiClient = class BaasMeldingDefaultApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async getMeldingen(urlPart) {
		const url = concatUrl(this.state.location.apiRoot.url, `/baas/meldingv2/for-url?pattern=${encodeURI(urlPart)}`);
		return (await this.axios.get(url)).data.map((melding) => ({
			...melding,
			baas: true
		}));
	}
	async hasBlockingMelding(urlPart) {
		const url = this.state.location.apiRoot.url + `baas/melding/important-only/for-url?urlPart=${encodeURI(urlPart)}`;
		return (await this.axios.get(url)).data;
	}
};
BaasMeldingDefaultApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BaasMeldingDefaultApiClient);
let BaasSecurityDefaultApiClient = class BaasSecurityDefaultApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async checkActieAllowed(actie) {
		let voorwieSegment = "";
		if (actie.voorwie && actie.voorwieRolcode) voorwieSegment = "/voorwie/" + actie.voorwie + "/rolcode/" + actie.voorwieRolcode;
		let hoedanigheidSegment = "";
		if (actie.hoedanigheid && actie.hoedanigheidRolcode) hoedanigheidSegment = "/hoedanigheid/" + actie.hoedanigheid + "/rolcode/" + actie.hoedanigheidRolcode;
		const url = this.state.location.apiRoot.url + "baas/security/isactieallowed/actie/" + encodeURI(actie.actie) + voorwieSegment + hoedanigheidSegment;
		return (await this.axios.get(url)).data;
	}
	async getUserInfo() {
		const rootUrl = this.state.location.apiRoot.url;
		try {
			const data = (await this.axios.get(rootUrl + "baas/security/user-info")).data;
			return {
				cbiUserId: data.cbiUserId,
				hoedanigheidInfoSet: data.hoedList,
				contactInfo: data.contactInfo != null ? {
					klantnummer: data.contactInfo.alvNr,
					firstname: data.contactInfo.firstName,
					lastname: data.contactInfo.lastname,
					fullName: data.contactInfo.fullname,
					phone: data.contactInfo.phoneNumber,
					mobile: data.contactInfo.mobileNumber,
					email: data.contactInfo.mailAddress
				} : void 0
			};
		} catch (error) {
			console.error(error);
			return {};
		}
	}
};
BaasSecurityDefaultApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BaasSecurityDefaultApiClient);
let BarAuditApiClient = class BarAuditApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async save(auditItems) {
		await this.axios.post(this.state.location.barWeb.url + "api/audit", auditItems);
	}
};
BarAuditApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarAuditApiClient);
let BarCommonApiClient = class BarCommonApiClient {
	constructor(state, axiosBaas, axiosPlain) {
		this.state = state;
		this.axiosBaas = axiosBaas;
		this.axiosPlain = axiosPlain;
	}
	async preInitLocal() {
		return (await this.axiosPlain.post(this.state.location.apiRoot.url + "api/pre-init-local", null, { withCredentials: true })).data;
	}
	async preInit(request) {
		const response = await this.axiosPlain.post(this.state.location.baasStatic.url + "baas/bootloader/pre-init", request, { withCredentials: true });
		if (response.data?.toepassing) this.parseToepassing(response.data.toepassing);
		return response.data;
	}
	async init(request) {
		const response = await this.axiosBaas.post(this.state.location.baasStatic.url + "baas/bootloader/init", request);
		if (response.data?.toepassing) this.parseToepassing(response.data.toepassing);
		return response.data;
	}
	async initLocal() {
		return (await axios.post(this.state.location.apiRoot.url + "api/init-local", {}, {
			withCredentials: true,
			headers: { "X-Requested-With": "XMLHttpRequest" }
		})).data;
	}
	async saveEndpoints(appCode, appVersion, endpoints) {
		const url = `${this.state.location.baasStatic.url}baas/bootloader/endpoints/appcode/${appCode}/version/${appVersion}`;
		try {
			const response = await this.axiosBaas.post(url, endpoints);
			if (!response.data?.returnVal) console.error("An error occurred while saving the endpoints", response.data);
		} catch (err) {
			console.error("An error occurred while saving the endpoints", err);
		}
	}
	parseToepassing(toepassing) {
		if (typeof toepassing.securityLevel === "string") {
			const securityLevelKey = "securityLevel";
			toepassing[securityLevelKey] = this.parseSecLevelFromString(toepassing.securityLevel);
		}
	}
	parseSecLevelFromString(securityLevel) {
		if (securityLevel == null) return null;
		switch (securityLevel.toUpperCase()) {
			case "DEV": return BaasSecurityLevel.Development;
			case "TST": return BaasSecurityLevel.Test;
			case "PRD": return BaasSecurityLevel.Production;
			default: return BaasSecurityLevel.Unknown;
		}
	}
};
BarCommonApiClient = __decorate([
	injectable(),
	__param(1, inject(BAAS_TYPES.AxiosInstance)),
	__param(2, inject(BAAS_TYPES.AxiosInstance)),
	__param(2, named(BAAS_AXIOS_INSTANCE_TYPES.Plain)),
	__metadata("design:paramtypes", [
		BaasAppState,
		Function,
		Function
	])
], BarCommonApiClient);
let BarLoggingApiClient = class BarLoggingApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async save(logs) {
		await this.axios.post(this.state.location.barWeb.url + "api/logging", logs);
	}
};
BarLoggingApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarLoggingApiClient);
let BarMeetpuntApiClient = class BarMeetpuntApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async save(meetpunten) {
		await this.axios.post(this.state.location.barWeb.url + "api/meetpunt", meetpunten);
	}
};
BarMeetpuntApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarMeetpuntApiClient);
let BarMeldingApiClient = class BarMeldingApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async getMeldingen(url) {
		return (await this.axios.post(this.state.location.barWeb.url + "api/meldingen", { url })).data.map((melding) => ({
			...melding,
			baas: false
		}));
	}
	async hasBlockingMelding(url) {
		return (await this.axios.post(this.state.location.barWeb.url + "api/meldingen/get", { url })).data;
	}
};
BarMeldingApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__param(0, named(BAAS_AXIOS_INSTANCE_TYPES.Baas)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarMeldingApiClient);
let BarProfielApiClient = class BarProfielApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async read(model) {
		return (await this.axios.post(this.state.location.barWeb.url + "api/profiel/read", model)).data;
	}
};
BarProfielApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarProfielApiClient);
let BarSecurityApiClient = class BarSecurityApiClient {
	constructor(axios, state) {
		this.axios = axios;
		this.state = state;
	}
	async checkActieAllowed(actie) {
		const url = this.state.location.barWeb.url + "api/security/check-actie-allowed";
		return (await this.axios.post(url, actie)).data;
	}
	async getUserInfo() {
		const url = this.state.location.baasStatic.url + "baas/security/user-info";
		try {
			const data = (await this.axios.get(url)).data;
			return {
				cbiUserId: data.cbiUserId,
				hoedanigheidInfoSet: data.hoedList,
				contactInfo: data.contactInfo != null ? {
					klantnummer: data.contactInfo.alvNr,
					firstname: data.contactInfo.firstName,
					lastname: data.contactInfo.lastname,
					fullName: data.contactInfo.fullname,
					phone: data.contactInfo.phoneNumber,
					mobile: data.contactInfo.mobileNumber,
					email: data.contactInfo.mailAddress
				} : void 0
			};
		} catch (error) {
			console.error(error);
			return {};
		}
	}
};
BarSecurityApiClient = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.AxiosInstance)),
	__metadata("design:paramtypes", [Function, BaasAppState])
], BarSecurityApiClient);
let BaasExternalAuthStrategy = class BaasExternalAuthStrategy {
	get authenticated() {
		return this.contextManager.checkBaasContextInStorage();
	}
	constructor(contextManager, contextProvider) {
		this.contextManager = contextManager;
		this.contextProvider = contextProvider;
	}
	async authenticate() {
		if (!this.contextProvider) throw "BaasExternalAuthStrategy was called but the injected BaasContextProvider has not been defined.";
		try {
			const context = await this.contextProvider.provide();
			if (context) {
				const userVoorwie = context.voorwie && context.voorwieRolcode ? `${context.voorwie};${context.voorwieRolcode.toUpperCase()}` : void 0;
				const userHoedanigheid = context.hoedanigheid && context.hoedanigheidRolcode ? `${context.hoedanigheid};${context.hoedanigheidRolcode.toUpperCase()}` : void 0;
				const state = {
					userTicketGuid: context.securityTicketId,
					userVoorwie,
					userHoedanigheid
				};
				this.contextManager.saveBaasContextToStorage(state);
				return true;
			} else return null;
		} catch (error) {
			console.error(error);
			return false;
		}
	}
	performLogOff() {
		this.contextManager.removeContextFromSession();
		return Promise.resolve();
	}
};
BaasExternalAuthStrategy = __decorate([
	injectable(),
	__param(1, inject(BAAS_TYPES.BaasContextProvider)),
	__metadata("design:paramtypes", [_BaasContextManager, Object])
], BaasExternalAuthStrategy);
let BaasIdpAuthStrategy = class BaasIdpAuthStrategy {
	get authenticated() {
		return this.idpClient.authenticated;
	}
	constructor(appState, bootstrapOptions, idpClient, appInfo) {
		this.appState = appState;
		this.bootstrapOptions = bootstrapOptions;
		this.idpClient = idpClient;
		this.appInfo = appInfo;
	}
	async authenticate() {
		window.localStorage.removeItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
		if (parseQueryStringValue("idp-status") === "fail") return false;
		else {
			await this.idpClient.authenticate(this.bootstrapOptions.auth.config);
			return null;
		}
	}
	async postProcessing() {
		if (!this.authenticated) return;
		const impersonateStrQuery = parseQueryStringValue("idp-impersonate");
		const impersonateStrStorage = window.localStorage.getItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE);
		let impersonate;
		if (impersonateStrQuery && (!impersonateStrStorage || impersonateStrStorage === false.toString())) {
			impersonate = impersonateStrQuery.toLowerCase() === true.toString();
			window.localStorage.setItem(_BAAS_SHARED_CONSTANTS.STORAGE_KEY.IDP_IMPERSONATE, impersonate.toString());
		} else impersonate = impersonateStrStorage === true.toString();
		const impersonateKey = "impersonate";
		this.appState.basiscontext[impersonateKey] = impersonate;
	}
	async performLogOff() {
		if (this.bootstrapOptions.localhost) this.appInfo.delete();
		await this.idpClient.logout();
	}
};
BaasIdpAuthStrategy = __decorate([injectable(), __metadata("design:paramtypes", [
	BaasAppState,
	BaasBootstrapOptions,
	BarIdpClient,
	_BarAppInfoManager
])], BaasIdpAuthStrategy);
let BaasIdpHeaderAuthStrategy = class BaasIdpHeaderAuthStrategy {
	constructor(idpClient, contextManager, bootstrapOptions, logOff) {
		this.idpClient = idpClient;
		this.contextManager = contextManager;
		this.bootstrapOptions = bootstrapOptions;
		this.logOff = logOff;
	}
	get authenticated() {
		return this.contextManager.checkBaasContextInStorage();
	}
	get authenticating() {
		return parseQueryStringValue("idp-status") != null;
	}
	async authenticate() {
		switch (parseQueryStringValue("idp-status")) {
			case "success": {
				const idpUsr = parseQueryStringValue("idp-usr");
				const voorwie = parseQueryStringValue("idp-vw");
				const voorwieRolcode = parseQueryStringValue("idp-vwrc");
				const userVoorwie = voorwie ? `${voorwie};${voorwieRolcode ? voorwieRolcode.toUpperCase() : "NULL"}` : void 0;
				const hoedanigheid = parseQueryStringValue("idp-hd");
				const hoedanigheidRolcode = parseQueryStringValue("idp-hdrc");
				const state = {
					userTicketGuid: idpUsr,
					userVoorwie,
					userHoedanigheid: hoedanigheid ? `${hoedanigheid};${hoedanigheidRolcode ? hoedanigheidRolcode.toUpperCase() : "NULL"}` : void 0
				};
				const url = clearQueryStringParams(window.location.href, (key) => key.startsWith("idp-"));
				window.history.replaceState({}, document.title, url.toString());
				this.contextManager.saveBaasContextToStorage(state);
				return true;
			}
			case "fail": return false;
			default:
				await this.idpClient.authenticate(this.bootstrapOptions.auth.config);
				return null;
		}
	}
	async performLogOff() {
		this.contextManager.removeContextFromSession();
		this.logOff.navigateLogOff();
	}
};
BaasIdpHeaderAuthStrategy = __decorate([injectable(), __metadata("design:paramtypes", [
	BarIdpClient,
	_BaasContextManager,
	BaasBootstrapOptions,
	_BaasLogOffUtility
])], BaasIdpHeaderAuthStrategy);
let _BaasProfielService = class _BaasProfielService {
	get store() {
		return this.cacheManager.getStore({
			storeType: BarCacheStoreType.User,
			store: "profiel",
			userId: this.state.basiscontext.userId
		});
	}
	constructor(state, cacheManager, apiClient) {
		this.state = state;
		this.cacheManager = cacheManager;
		this.apiClient = apiClient;
	}
	read(key) {
		const httpCallback = () => {
			const model = {
				key,
				appCode: this.state.toepassing.code,
				appVersion: this.state.toepassing.versie
			};
			return this.apiClient.read(model);
		};
		return this.store.getOrSetItem(key, httpCallback, { session: true });
	}
	async readNumber(key) {
		const value = await this.read(key);
		return value != null ? parseFloat(value) : null;
	}
	async readBoolean(key) {
		const value = await this.read(key);
		return value != null ? value.toLowerCase() == "true" || value == "1" : null;
	}
	flush() {
		return this.store.clear();
	}
};
_BaasProfielService = __decorate([
	injectable(),
	__param(2, inject(BAAS_TYPES.BaasConfigurationApiClient)),
	__metadata("design:paramtypes", [
		BaasAppState,
		BarCacheManager,
		Object
	])
], _BaasProfielService);
let BarGvkDefaultState = class BarGvkDefaultState {
	constructor(appState) {
		this.appState = appState;
	}
	get appCode() {
		return this.appState.toepassing?.code;
	}
	get userId() {
		return this.appState.basiscontext?.userId;
	}
	get apiRootUrl() {
		return this.appState.location?.barWeb?.url;
	}
};
BarGvkDefaultState = __decorate([injectable(), __metadata("design:paramtypes", [BaasAppState])], BarGvkDefaultState);
var BarGvkCompatibilityMode;
(function(BarGvkCompatibilityMode) {
	BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_1"] = 0] = "Version_1";
	BarGvkCompatibilityMode[BarGvkCompatibilityMode["Version_2"] = 1] = "Version_2";
})(BarGvkCompatibilityMode || (BarGvkCompatibilityMode = {}));
var BarGvkStorageMode;
(function(BarGvkStorageMode) {
	BarGvkStorageMode[BarGvkStorageMode["Default"] = 0] = "Default";
	BarGvkStorageMode[BarGvkStorageMode["BrowserOnly"] = 1] = "BrowserOnly";
	BarGvkStorageMode[BarGvkStorageMode["RemoteOnly"] = 2] = "RemoteOnly";
})(BarGvkStorageMode || (BarGvkStorageMode = {}));
var BarGvkDefaultStore = class {
	constructor(appCode, userId) {
		this.store = new BarCacheStore(DB_NAME_GVK, `${appCode}_${userId}`);
	}
	async getItem(key) {
		return await this.store.getItem(key);
	}
	async setItem(key, value) {
		await this.store.setItem(key, value, { session: false });
	}
	async removeItem(key) {
		await this.store.removeItem(key);
	}
};
const DB_NAME_GVK_LEGACY = "domain.gebruikersvoorkeuren";
var BarGvkLegacyStore = class {
	constructor(appCode, userId) {
		this.appCode = appCode;
		this.userId = userId;
	}
	getItem(key) {
		const realKey = this.generateRealKey(key);
		return this.getStoreInstance().getItem(realKey);
	}
	setItem(key, value) {
		const realKey = this.generateRealKey(key);
		return this.getStoreInstance().setItem(realKey, value);
	}
	removeItem(key) {
		const realKey = this.generateRealKey(key);
		return this.getStoreInstance().removeItem(realKey);
	}
	generateRealKey(key) {
		return `_${this.userId}_${key}`;
	}
	getStoreInstance() {
		return new BarStore(DB_NAME_GVK_LEGACY, this.appCode);
	}
};
let BarGvkInternalManager = class BarGvkInternalManager {
	constructor(remoteStoreProvider, bootstrapOptions) {
		this.remoteStoreProvider = remoteStoreProvider;
		this.bootstrapOptions = bootstrapOptions;
		this.disabled = false;
	}
	async checkDisabled(loadRemoteSettings, appCode, userId) {
		if (this.disabled) return true;
		const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, "_disabled"), localSessionValue = window.sessionStorage.getItem(uniqueKey);
		if (localSessionValue === void 0 || localSessionValue === null) {
			if (loadRemoteSettings) {
				const disabled = await this.getRemoteStore(appCode).checkDisabled();
				window.sessionStorage.setItem(uniqueKey, disabled.toString());
				return disabled;
			} else {
				sessionStorage.setItem(uniqueKey, "false");
				return false;
			}
		} else return localSessionValue === "true";
	}
	async getItem(appCode, userId, setName, storageMode, noSettingsAction, sets, forceGetFromRemoteStorage, compatibilityMode) {
		let value = void 0;
		if (storageMode !== BarGvkStorageMode.BrowserOnly) {
			const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName), localSessionValue = sessionStorage.getItem(uniqueKey);
			if (localSessionValue === void 0 || localSessionValue === null || forceGetFromRemoteStorage) {
				sessionStorage.setItem(uniqueKey, (/* @__PURE__ */ new Date()).getTime().toString());
				value = await this.getRemoteStore(appCode).getItem(setName);
				if (value != null) await this.setItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, value, compatibilityMode);
				else await this.removeItem(appCode, userId, setName, BarGvkStorageMode.BrowserOnly, compatibilityMode);
			}
		}
		if (storageMode !== BarGvkStorageMode.RemoteOnly && !value) value = await this.getBrowserStore(appCode, userId, compatibilityMode).getItem(setName);
		if (noSettingsAction.action === "callback" && !value) {
			const ctx = {
				appCode,
				setName,
				settings: sets != null && sets.get(setName) ? sets.get(setName).settings : this.createEmptySettings(setName)
			};
			const voorkeuren = await noSettingsAction.callback.apply(null, [ctx]);
			if (voorkeuren) {
				let voorkeurenObject, voorkeurenString;
				if (typeof voorkeuren === "string") {
					voorkeurenObject = JSON.parse(voorkeuren);
					voorkeurenString = voorkeuren;
				} else if (voorkeuren instanceof String) {
					voorkeurenObject = JSON.parse(voorkeuren.toString());
					voorkeurenString = voorkeuren;
				} else {
					voorkeurenObject = voorkeuren;
					voorkeurenString = JSON.stringify(voorkeuren);
				}
				if (voorkeurenObject._gvk_setName && voorkeurenObject._gvk_timeStamp) {
					await this.setItem(appCode, userId, setName, storageMode, voorkeurenString, compatibilityMode);
					value = voorkeurenString;
				}
			}
		}
		return value;
	}
	async setItem(appCode, userId, setName, storageMode, voorkeuren, compatibilityMode) {
		if (storageMode == BarGvkStorageMode.BrowserOnly) await this.getBrowserStore(appCode, userId, compatibilityMode).setItem(setName, voorkeuren);
		else {
			await this.getBrowserStore(appCode, userId, compatibilityMode).setItem(setName, voorkeuren);
			const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
			sessionStorage.setItem(uniqueKey, (/* @__PURE__ */ new Date()).getTime().toString());
			await this.getRemoteStore(appCode).setItem(setName, JSON.parse(voorkeuren));
		}
	}
	async removeItem(appCode, userId, setName, storageMode, compatibilityMode) {
		await this.getBrowserStore(appCode, userId, compatibilityMode).removeItem(setName);
		if (storageMode != BarGvkStorageMode.BrowserOnly) {
			const uniqueKey = this.getUniqueKeyForSessionStorage(appCode, userId, setName);
			if (appCode === null || userId === null || setName === null) {
				let i;
				const keysToRemove = [];
				for (i = 0; i < sessionStorage.length; i++) if (sessionStorage.key(i).indexOf(uniqueKey) == 0) keysToRemove.push(sessionStorage.key(i));
				for (i = 0; i < keysToRemove.length; i++) sessionStorage.removeItem(keysToRemove[i]);
			} else sessionStorage.removeItem(uniqueKey);
			await this.getRemoteStore(appCode).removeItem(setName);
		}
	}
	createEmptySettings(setName) {
		return {
			_gvk_setName: setName,
			_gvk_timeStamp: null
		};
	}
	getUniqueKeyForSessionStorage(appCode, userId, setName) {
		let key = "domain.gvk";
		if (appCode) {
			key = key + "_" + appCode;
			if (userId) {
				key = key + "_" + userId;
				if (setName) key = key + "_" + setName;
			}
		}
		return key;
	}
	getBrowserStore(appCode, userId, compatibilityMode) {
		if (this.bootstrapOptions.cacheStorage?.type === "none") return new BaasNullCacheStore();
		if (compatibilityMode == BarGvkCompatibilityMode.Version_1) return new BarGvkLegacyStore(appCode, userId);
		else return new BarGvkDefaultStore(appCode, userId);
	}
	getRemoteStore(appCode) {
		return this.remoteStoreProvider.provide(appCode);
	}
};
BarGvkInternalManager = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.BarGvkRemoteStoreProvider)),
	__metadata("design:paramtypes", [Object, BaasBootstrapOptions])
], BarGvkInternalManager);
let BarGvkManager = class BarGvkManager {
	set disabled(value) {
		this.storage.disabled = value;
	}
	get disabled() {
		return this.storage.disabled;
	}
	constructor(state, storage, bootstrapOptions) {
		this.state = state;
		this.storage = storage;
		this.bootstrapOptions = bootstrapOptions;
		this.settings = { storageMode: BarGvkStorageMode.Default };
		if (this.bootstrapOptions.public || this.bootstrapOptions.connectivity === BaasConnectivity.Offline) this.settings.storageMode = BarGvkStorageMode.BrowserOnly;
	}
	checkDisabled(appCode = this.state.appCode) {
		return this.storage.checkDisabled(true, appCode, this.state.userId);
	}
	async getItem(key, settings = this.settings) {
		const strData = await this.storage.getItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, { action: "none" }, null, settings.storageMode == BarGvkStorageMode.RemoteOnly, settings.compatibilityMode);
		return strData == null ? null : JSON.parse(strData);
	}
	async setItem(key, value, settings = this.settings) {
		if (value && typeof value !== "object") throw new Error("GVK item value must be a JS object.");
		await this.storage.setItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, value == null ? null : JSON.stringify(value), settings.compatibilityMode);
	}
	async removeItem(key, settings = this.settings) {
		await this.storage.removeItem(settings.appCode || this.state.appCode, this.state.userId, key, settings.storageMode, settings.compatibilityMode);
	}
};
BarGvkManager = __decorate([
	injectable(),
	__param(0, inject(_BAAS_TYPES.BarGvkState)),
	__metadata("design:paramtypes", [
		Object,
		BarGvkInternalManager,
		BaasBootstrapOptions
	])
], BarGvkManager);
var BarGvkRemoteStore = class {
	get urlRoot() {
		let result = this.state.apiRootUrl || "";
		if (!result.endsWith("/")) result += "/";
		result += "api/gvk";
		return result;
	}
	constructor(state, axios, appCode) {
		this.state = state;
		this.axios = axios;
		this.appCode = appCode;
	}
	async checkDisabled() {
		try {
			const response = await this.axios.get(`${this.urlRoot}/${this.appCode}/settings`);
			return response.data?.disabled == null ? true : response.data.disabled;
		} catch (ex) {
			console.error(ex);
			return true;
		}
	}
	async getItem(key) {
		const response = await this.axios.get(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`);
		return response.data.hasVoorkeuren ? response.data.voorkeuren : void 0;
	}
	async setItem(key, value) {
		try {
			await this.axios.put(`${this.urlRoot}/${this.appCode}?key=${encodeURIComponent(key)}`, value);
		} catch (error) {
			console.error("Server side fout bij wegschrijven voorkeuren", error);
		}
	}
	async removeItem(key) {
		let optionalSetName = "";
		if (key) optionalSetName = `?key=${encodeURIComponent(key)}`;
		await this.axios.delete(`${this.urlRoot}/${this.appCode}${optionalSetName}`);
	}
};
let BarGvkDefaultRemoteStoreProvider = class BarGvkDefaultRemoteStoreProvider {
	constructor(state, axios) {
		this.state = state;
		this.axios = axios;
	}
	provide(appCode) {
		return new BarGvkRemoteStore(this.state, this.axios, appCode);
	}
};
BarGvkDefaultRemoteStoreProvider = __decorate([
	injectable(),
	__param(0, inject(_BAAS_TYPES.BarGvkState)),
	__param(1, inject(BAAS_TYPES.AxiosInstance)),
	__param(1, named(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent)),
	__metadata("design:paramtypes", [Object, Function])
], BarGvkDefaultRemoteStoreProvider);
var BarClearStateTarget;
(function(BarClearStateTarget) {
	BarClearStateTarget[BarClearStateTarget["Baas"] = 0] = "Baas";
	BarClearStateTarget[BarClearStateTarget["Full"] = 1] = "Full";
	BarClearStateTarget[BarClearStateTarget["Configuration"] = 2] = "Configuration";
	BarClearStateTarget[BarClearStateTarget["Security"] = 3] = "Security";
	BarClearStateTarget[BarClearStateTarget["CacheStorage"] = 4] = "CacheStorage";
	BarClearStateTarget[BarClearStateTarget["CacheStorageApp"] = 5] = "CacheStorageApp";
	BarClearStateTarget[BarClearStateTarget["CacheStorageAppUser"] = 6] = "CacheStorageAppUser";
	BarClearStateTarget[BarClearStateTarget["CacheStorageUser"] = 7] = "CacheStorageUser";
	BarClearStateTarget[BarClearStateTarget["CacheStorageGlobal"] = 8] = "CacheStorageGlobal";
	BarClearStateTarget[BarClearStateTarget["Gvk"] = 9] = "Gvk";
	BarClearStateTarget[BarClearStateTarget["LocalStorage"] = 10] = "LocalStorage";
	BarClearStateTarget[BarClearStateTarget["SessionStorage"] = 11] = "SessionStorage";
	BarClearStateTarget[BarClearStateTarget["IndexedDb"] = 12] = "IndexedDb";
	BarClearStateTarget[BarClearStateTarget["Cookies"] = 13] = "Cookies";
})(BarClearStateTarget || (BarClearStateTarget = {}));
let BarClearStateHubMessageHandler = class BarClearStateHubMessageHandler {
	constructor() {
		this.action = "ClearState";
	}
	async handle(target) {
		switch (target) {
			case BarClearStateTarget.Baas:
				this.#clearCacheStorage();
				this.#clearGvk(false);
				this.#clearStorageByPrefix(window.sessionStorage, "domain.gvk_");
				this.#clearStorageByPrefix(window.localStorage, "lvflanders.bar.ng.core");
				this.#clearStorageByPrefix(window.sessionStorage, "lvflanders.bar.ng.core");
				this.#clearStorageByPrefix(window.localStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX);
				this.#clearStorageByPrefix(window.sessionStorage, _BAAS_SHARED_CONSTANTS.STORAGE_KEY_PREFIX);
				break;
			case BarClearStateTarget.Cookies:
				deleteAllCookies();
				break;
			case BarClearStateTarget.SessionStorage:
				window.sessionStorage.clear();
				break;
			case BarClearStateTarget.LocalStorage:
				window.localStorage.clear();
				break;
			case BarClearStateTarget.IndexedDb:
				deleteAllIndexedDb();
				break;
			case BarClearStateTarget.Full:
				deleteAllCookies();
				window.sessionStorage.clear();
				window.localStorage.clear();
				deleteAllIndexedDb();
				break;
			case BarClearStateTarget.Gvk:
				this.#clearGvk();
				break;
			case BarClearStateTarget.CacheStorageGlobal:
				deleteIndexedDb(DB_NAME_GLOBAL);
				break;
			case BarClearStateTarget.CacheStorageUser:
				deleteIndexedDb(DB_NAME_USER);
				break;
			case BarClearStateTarget.CacheStorageApp:
				deleteIndexedDb(DB_NAME_APP);
				break;
			case BarClearStateTarget.CacheStorageAppUser:
				deleteIndexedDb(DB_NAME_APP_USER);
				break;
			case BarClearStateTarget.CacheStorage:
				this.#clearCacheStorage();
				break;
			case BarClearStateTarget.Configuration:
				await this.#clearUserObjectStores("profiel", "configuration");
				break;
			case BarClearStateTarget.Security: await this.#clearUserObjectStores("actieAllowed");
		}
	}
	async #clearUserObjectStores(...objectStoreNameSuffixes) {
		const info = await getIndexedDbInfo(DB_NAME_USER);
		const objectStoresToDelete = info.objectStoreNames.filter((storeName) => {
			return objectStoreNameSuffixes.some((suffix) => storeName.endsWith(`_${suffix}`));
		});
		if (objectStoresToDelete.length) await _deleteIndexedDbObjectStores(DB_NAME_USER, info.version + 1, objectStoresToDelete);
	}
	#clearCacheStorage() {
		deleteIndexedDb(DB_NAME_APP);
		deleteIndexedDb(DB_NAME_APP_USER);
		deleteIndexedDb(DB_NAME_USER);
		deleteIndexedDb(DB_NAME_GLOBAL);
		deleteIndexedDb(DB_NAME_GVK);
	}
	#clearGvk(clearNonLegacy = true) {
		deleteIndexedDb(DB_NAME_GVK_LEGACY);
		if (clearNonLegacy) deleteIndexedDb(DB_NAME_GVK);
		this.#clearStorageByPrefix(window.sessionStorage, "domain.gvk_");
	}
	#clearStorageByPrefix(storage, keyPrefix) {
		Object.keys(storage).filter((key) => key.startsWith(keyPrefix)).forEach((key) => {
			storage.removeItem(key);
		});
	}
};
BarClearStateHubMessageHandler = __decorate([injectable()], BarClearStateHubMessageHandler);
let BarCustomHubMessageHandler = class BarCustomHubMessageHandler {
	constructor() {
		this.action = "CustomMessage";
	}
	handle(data) {
		const customEvent = new CustomEvent(BAAS_CUSTOM_EVENT.CUSTOM_HUB_MESSAGE, { detail: data });
		window.dispatchEvent(customEvent);
	}
};
BarCustomHubMessageHandler = __decorate([injectable()], BarCustomHubMessageHandler);
let BarLogOffHubMessageHandler = class BarLogOffHubMessageHandler {
	constructor(auth) {
		this.auth = auth;
		this.action = "LogOff";
	}
	async handle() {
		await this.auth.logOff();
	}
};
BarLogOffHubMessageHandler = __decorate([injectable(), __metadata("design:paramtypes", [BaasAuthService])], BarLogOffHubMessageHandler);
let BarPushNotificationHubMessageHandler = class BarPushNotificationHubMessageHandler {
	constructor(notificationManager) {
		this.notificationManager = notificationManager;
		this.action = "PushNotification";
	}
	handle(data) {
		this.notificationManager.show(data);
	}
};
BarPushNotificationHubMessageHandler = __decorate([injectable(), __metadata("design:paramtypes", [BarNotificationManager])], BarPushNotificationHubMessageHandler);
let BaasMeldingService = class BaasMeldingService {
	#apiClient;
	constructor(apiClient) {
		this.#apiClient = apiClient;
	}
	getMeldingen(url) {
		return this.#apiClient.getMeldingen(url);
	}
	hasBlockingMelding(url) {
		return this.#apiClient.hasBlockingMelding(url);
	}
};
BaasMeldingService = __decorate([
	injectable(),
	__param(0, inject(BAAS_TYPES.BaasMeldingApiClient)),
	__metadata("design:paramtypes", [Object])
], BaasMeldingService);
let _BaasTransportHelper = class _BaasTransportHelper {
	#options;
	#appState;
	#baasContextManager;
	constructor(options, appState, baasContextManager) {
		this.#options = options;
		this.#appState = appState;
		this.#baasContextManager = baasContextManager;
	}
	generateAppStateHeaders() {
		const result = {};
		if (this.#options.auth.withActingScopeHeaders && this.#appState.basiscontext) {
			result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_VOORWIE_TOKEN] = JSON.stringify({
				value: this.#appState.basiscontext.voorwie || "",
				rolcode: this.#appState.basiscontext.voorwieRolcode || ""
			});
			result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_HOEDANIGHEID_TOKEN] = JSON.stringify({
				value: this.#appState.basiscontext.hoedanigheid || "",
				rolcode: this.#appState.basiscontext.hoedanigheidRolcode || ""
			});
		}
		const callStack = [{
			userId: this.#appState.basiscontext?.userId,
			appCode: this.#appState.toepassing?.code
		}];
		const headerValue = JSON.stringify(callStack);
		result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BROWSER_CALLSTACK] = headerValue;
		result[_BAAS_SHARED_CONSTANTS.HEADER.BAAS_BROWSER_CALLSTACK_V2] = headerValue;
		return encodeHeaders(result);
	}
	generateBaasInterceptionHeaders() {
		const appStateHeaders = this.generateAppStateHeaders();
		if (this.#options.auth.contextTransportModus === BaasContextTransportModus.Header && this.#baasContextManager.checkBaasContextInStorage()) return {
			...generateBaasAuthHeaders(this.#baasContextManager.getBaasContextFromStorage()),
			...appStateHeaders
		};
		return { ...appStateHeaders };
	}
};
_BaasTransportHelper = __decorate([injectable(), __metadata("design:paramtypes", [
	BaasBootstrapOptions,
	BaasAppState,
	_BaasContextManager
])], _BaasTransportHelper);
let BaasTransportHelper = class BaasTransportHelper {
	#contextService;
	#transportHelper;
	constructor(contextService, transportHelper) {
		this.#contextService = contextService;
		this.#transportHelper = transportHelper;
	}
	async generateBaasHeaders() {
		const authHeaders = generateBaasAuthHeaders(await this.#contextService.getStateContext());
		const appStateHeaders = this.#transportHelper.generateAppStateHeaders();
		return {
			...authHeaders,
			...appStateHeaders
		};
	}
};
BaasTransportHelper = __decorate([injectable(), __metadata("design:paramtypes", [BaasContextService, _BaasTransportHelper])], BaasTransportHelper);
var BaasApmTransaction = class {
	constructor(transaction) {
		this.transaction = transaction;
		if (!transaction) throw new Error("Transaction cannot be null or undefined");
	}
	startSpan(name, type, options) {
		const span = this.transaction.startSpan(name, type, { startTime: options?.startTime });
		return span ? new BaasApmSpan(span) : BaasNoopTelemetrySpan.INSTANCE;
	}
	end() {
		this.transaction.end();
	}
};
var BaasNoopTelemetryTransaction = class BaasNoopTelemetryTransaction {
	static {
		this.INSTANCE = new BaasNoopTelemetryTransaction();
	}
	startSpan(name, type, options) {
		return BaasNoopTelemetrySpan.INSTANCE;
	}
	end() {}
};
var BaasNoopTelemetrySpan = class BaasNoopTelemetrySpan {
	static {
		this.INSTANCE = new BaasNoopTelemetrySpan();
	}
	end() {}
	addLabels() {}
};
var BaasApmSpan = class {
	constructor(span) {
		this.span = span;
	}
	end() {
		this.span.end();
	}
	addLabels(labels) {
		this.span.addLabels(labels);
	}
};
let BaasApmTelemetry = class BaasApmTelemetry {
	constructor(baasApm) {
		this.baasApm = baasApm;
	}
	startTransaction(name, type) {
		if (this.#enabled) return new BaasApmTransaction(this.baasApm.startTransaction(name, type));
		return BaasNoopTelemetryTransaction.INSTANCE;
	}
	get currentTransaction() {
		if (this.#enabled) {
			const transaction = this.baasApm.apm?.getCurrentTransaction();
			if (transaction) return new BaasApmTransaction(transaction);
		}
	}
	startSpan(name, type) {
		const span = this.baasApm.apm.startSpan(name, type);
		return span ? new BaasApmSpan(span) : BaasNoopTelemetrySpan.INSTANCE;
	}
	get #enabled() {
		return this.baasApm.active && this.baasApm.apm !== void 0;
	}
	handleError(error) {
		this.baasApm.handleError(error);
	}
};
BaasApmTelemetry = __decorate([injectable(), __metadata("design:paramtypes", [_BaasApm])], BaasApmTelemetry);
function _registerTelemetryIoc(container) {
	container.bind(BAAS_TYPES.BaasTelemetry).to(BaasApmTelemetry).inSingletonScope();
}
let BaasToepassingService = class BaasToepassingService {
	constructor(appInfo, state) {
		this.appInfo = appInfo;
		this.state = state;
	}
	async setVoorwie(voorwie, voorwieRolcode) {
		if (voorwie == null) voorwie = "";
		if (voorwieRolcode == null) voorwieRolcode = "";
		await this.setBasiscontextData((basiscontext) => {
			basiscontext.voorwie = voorwie;
			basiscontext.voorwieRolcode = voorwieRolcode;
		}, "Set-Voorwie");
	}
	async resetVoorwie() {
		await this.setVoorwie("", "");
	}
	async setHoedanigheid(hoedanigheid, hoedanigheidRolcode) {
		if (hoedanigheid == null) hoedanigheid = "";
		if (hoedanigheidRolcode == null) hoedanigheidRolcode = "";
		await this.setBasiscontextData((basiscontext) => {
			basiscontext.hoedanigheid = hoedanigheid;
			basiscontext.hoedanigheidRolcode = hoedanigheidRolcode;
		}, "Set-Hoedanigheid");
	}
	async resetHoedanigheid() {
		await this.setHoedanigheid("", "");
	}
	async setBasiscontextData(handler, method) {
		const basiscontext = this.state.basiscontext;
		if (basiscontext != null) {
			handler(basiscontext);
			const basiscontextKey = "basiscontext";
			this.state[basiscontextKey] = basiscontext;
			await this.appInfo.persistBasiscontext();
			const customEvent = new CustomEvent(BAAS_CUSTOM_EVENT.BASISCONTEXT_CHANGED, { detail: basiscontext });
			window.dispatchEvent(customEvent);
		}
	}
};
BaasToepassingService = __decorate([injectable(), __metadata("design:paramtypes", [_BarAppInfoManager, BaasAppState])], BaasToepassingService);
const ANTI_XSSI_TOKEN = ")]}',";
let BaasInterceptor = class BaasInterceptor {
	#options;
	#appState;
	#appInfo;
	#authFailedStrategy;
	#telemetry;
	#transportHelper;
	get handleAuthFailedStatus() {
		return !this.#appState.config || this.#appState.config.interception?.handleAuthFailed;
	}
	get handleForbiddenStatus() {
		return this.#appState.config?.interception != null && this.#appState.config.interception?.handleForbidden;
	}
	constructor(options, appState, appInfo, authFailedStrategy, telemetry, transportHelper) {
		this.silent = false;
		this.#options = options;
		this.#appState = appState;
		this.#appInfo = appInfo;
		this.#authFailedStrategy = authFailedStrategy;
		this.#telemetry = telemetry;
		this.#transportHelper = transportHelper;
	}
	async onRequest(config) {
		const stopwatch = new BarStopwatch();
		stopwatch.start();
		config.metadata = { stopwatch };
		const domainRequest = this.checkDomainRequest(config.url);
		const crossDomainRequest = this.checkCrossDomainRequest(config.url);
		if (domainRequest || crossDomainRequest) {
			if (this.#appState.basiscontext != null) await this.#appInfo.persistBasiscontext();
			config.withCredentials = true;
			this.defineHeaders(config);
		}
		this.telemetryStart(config);
		return Promise.resolve(config);
	}
	async onResponse(response) {
		this.telemetryEnd(response.config);
		if (response.data && typeof response.data === "string" && response.data.startsWith(ANTI_XSSI_TOKEN)) {
			const cleanData = response.data.substring(5);
			response.data = JSON.parse(cleanData);
		}
		if (this.checkDomainRequest(response.request.url) && this.#options.auth?.syncBasiscontextAfterRequestCompletion) {
			const basiscontext = this.#appInfo.getBasiscontext();
			const basiscontextKey = "basiscontext";
			this.#appState[basiscontextKey] = basiscontext;
		}
		return Promise.resolve(response);
	}
	async onError(error) {
		this.telemetryEnd(error.config);
		if (!this.silent) {
			if (this.handleAuthFailedStatus && error.response?.status == 455) await this.handleAuthFailed(error);
			else if (this.handleForbiddenStatus && !this.#options.localhost && error.response?.status == 403) this.handleForbidden();
		}
		return Promise.reject(error);
	}
	checkDomainRequest(url) {
		return !checkUrlAbsolute(url) || url.indexOf("localhost") > -1 || url.startsWith(document.location.origin) || _checkCompatibleOrigin(url, this.#options.compatibleOrigins);
	}
	checkCrossDomainRequest(url) {
		const result = false;
		if (this.#options.localhost) return extractUrlParts(url).origin == this.#appState.location.release.origin && this.#appState.location.release.hostname != "localhost";
		return result;
	}
	defineHeaders(config) {
		config.headers = {
			...config.headers,
			"X-Requested-With": "XMLHttpRequest"
		};
		const requestToken = this.#appState.requestToken;
		if (requestToken) config.headers = {
			...config.headers,
			"X-XSRF-TOKEN": requestToken
		};
		let auditId = null;
		let auditType = null;
		if (this.#appState.routeId != null) {
			auditId = this.#appState.routeId;
			auditType = BaasAuditType.Route;
		} else if (this.#appState.pageId != null) {
			auditId = this.#appState.pageId;
			auditType = BaasAuditType.Page;
		} else if (this.#appState.sessionId != null) {
			auditId = this.#appState.sessionId;
			auditType = BaasAuditType.Session;
		}
		if (auditId) config.headers = {
			...config.headers,
			"X-Audit-Id": auditType
		};
		this.addBaasHeaders(config);
	}
	addBaasHeaders(config) {
		const baasHeaders = this.#transportHelper.generateBaasInterceptionHeaders();
		this.assignHeaders(config, baasHeaders);
	}
	assignHeaders(config, headers) {
		for (const [key, value] of Object.entries(headers)) if (!config.headers[key]) config.headers[key] = value;
	}
	async handleAuthFailed(error) {
		const context = {
			error,
			request: error.config?.url ? {
				method: error.config.method,
				url: error.config.url
			} : void 0
		};
		await this.#authFailedStrategy.handle(context);
	}
	handleForbidden() {
		window.location.replace(this.#appState.location.baasStatic.url + _BAAS_SHARED_CONSTANTS.PAGE.INSUFFICIENT_PRIVILEGES);
	}
	telemetryStart(config) {
		const name = `Axios HTTP ${config.method} ${config.url}`;
		const telemetry = {};
		if (this.#telemetry.currentTransaction) telemetry.span = this.#telemetry.currentTransaction.startSpan(name, "axios-http-request");
		else {
			telemetry.transaction = this.#telemetry.startTransaction(name, "axios-http-request");
			telemetry.span = telemetry.transaction.startSpan(name, "axios-http-request");
		}
		config.telemetry = telemetry;
	}
	telemetryEnd(config) {
		if (config?.telemetry) {
			config.telemetry.span?.end();
			if (config.telemetry.transaction) config.telemetry.transaction.end();
		}
	}
};
BaasInterceptor = __decorate([
	injectable(),
	__param(4, inject(BAAS_TYPES.BaasTelemetry)),
	__metadata("design:paramtypes", [
		BaasBootstrapOptions,
		BaasAppState,
		_BarAppInfoManager,
		_BaasAuthFailedOrchestrator,
		Object,
		_BaasTransportHelper
	])
], BaasInterceptor);
function bootstrapBaasIoc(options) {
	const container = new Container();
	baasContext.container = container;
	container.bind(Container).toConstantValue(container);
	container.bind(BaasAppState).toSelf().inSingletonScope();
	container.bind(BaasBrowserCapabilities).toSelf().inSingletonScope();
	container.bind(BaasBootstrap).toSelf().inSingletonScope();
	container.bind(BaasInterceptor).toSelf();
	container.bind(BaasToepassingService).toSelf().inSingletonScope();
	container.bind(BaasBootstrapOptions).toConstantValue(options);
	container.bind(BarRapUrlHelper).toSelf().inSingletonScope();
	container.bind(BaasLoggingService).toSelf().inSingletonScope();
	container.bind(_BaasLogOffUtility).toSelf().inSingletonScope();
	container.bind(BaasSecurityService).toSelf().inSingletonScope();
	container.bind(BaasAuditService).toSelf().inSingletonScope();
	container.bind(BaasMeldingService).toSelf().inSingletonScope();
	container.bind(BaasConfigurationService).toSelf().inSingletonScope();
	container.bind(_BaasProfielService).toSelf().inSingletonScope();
	container.bind(BaasMeetpuntService).toSelf().inSingletonScope();
	container.bind(BarNotificationManager).toSelf().inSingletonScope();
	container.bind(_OopsDomService).toSelf().inSingletonScope();
	container.bind(BaasSession).toSelf().inSingletonScope();
	container.bind(_BaasAuthFailedOrchestrator).toSelf().inSingletonScope();
	container.bind(_BaasApm).toSelf().inSingletonScope();
	container.bind(BaasTransportHelper).toSelf().inSingletonScope();
	container.bind(_BaasTransportHelper).toSelf().inSingletonScope();
	_registerTelemetryIoc(container);
	defineGvk(container);
	defineAuth(container, options.auth);
	defineBackendApi(container, options.backendType);
	defineI18n(container, options.i18n);
	defineAxios(container);
	defineCaching(container, options.appCode);
	defineHubStuff(container);
	return container;
}
function defineGvk(container) {
	container.bind(BarGvkManager).toSelf().inSingletonScope();
	container.bind(BarGvkInternalManager).toSelf().inSingletonScope();
	container.bind(_BAAS_TYPES.BarGvkState).to(BarGvkDefaultState).inSingletonScope();
	container.bind(BAAS_TYPES.BarGvkRemoteStoreProvider).to(BarGvkDefaultRemoteStoreProvider).inSingletonScope();
}
function defineAuth(container, auth) {
	container.bind(BaasContextService).toSelf().inSingletonScope();
	container.bind(_BaasContextManager).toSelf().inSingletonScope();
	container.bind(_BarAppInfoManager).toSelf().inSingletonScope();
	container.bind(BaasExternalAuthStrategy).toSelf().inSingletonScope();
	container.bind(BaasIdpHeaderAuthStrategy).toSelf().inSingletonScope();
	container.bind(BaasIdpAuthStrategy).toSelf().inSingletonScope();
	container.bind(BAAS_TYPES.BaasAuthStrategy).toDynamicValue((context) => {
		switch (auth.contextTransportModus) {
			case BaasContextTransportModus.Header:
				if (context.get(BAAS_TYPES.BaasContextProvider, { optional: true }) != null) return context.get(BaasExternalAuthStrategy);
				return context.get(BaasIdpHeaderAuthStrategy);
			default:
			case BaasContextTransportModus.Cookie: return context.get(BaasIdpAuthStrategy);
		}
	}).inSingletonScope();
	container.bind(BaasAuthService).toSelf().inSingletonScope();
	const appInfoManager = container.get(_BarAppInfoManager);
	const idpClientOptions = new BarIdpClientOptions();
	if (auth.contextTransportModus === BaasContextTransportModus.Header) idpClientOptions.formatToken = "qs";
	const idpClient = new BarIdpClient(idpClientOptions, appInfoManager);
	container.bind(BarIdpClient).toConstantValue(idpClient);
	container.bind(BAAS_TYPES._BaasLvOicdAuthService).to(BaasOidcAuthService).inSingletonScope();
}
function defineCaching(container, appCode) {
	container.bind(BarCacheManager).toSelf().inSingletonScope();
	container.bind(BarCacheStore).toDynamicValue((context) => {
		return context.get(BarCacheManager).getStore({ storeType: BarCacheStoreType.Global });
	}).whenTagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Global);
	if (appCode) container.bind(BarCacheStore).toDynamicValue((context) => {
		return context.get(BarCacheManager).getStore({
			storeType: BarCacheStoreType.Application,
			appCode
		});
	}).whenTagged(BAAS_METADATA_KEY.CACHE_STORE_TYPE_TAG, BarCacheStoreType.Application);
}
function defineBackendApi(container, backendType) {
	container.bind(_BaasStaticApiClient).toSelf().inSingletonScope();
	container.bind(_BaasStaticConfigurationApiClient).toSelf().inSingletonScope();
	container.bind(_BaasWebContextApiClient).toSelf().inSingletonScope();
	switch (backendType) {
		case BaasBackendType.Bar:
			container.bind(BAAS_TYPES.BaasAuditApiClient).to(BarAuditApiClient);
			container.bind(BAAS_TYPES.BaasLoggingApiClient).to(BarLoggingApiClient);
			container.bind(BAAS_TYPES.BaasMeldingApiClient).to(BarMeldingApiClient);
			container.bind(BAAS_TYPES.BaasSecurityApiClient).to(BarSecurityApiClient);
			container.bind(BAAS_TYPES.BaasConfigurationApiClient).to(BarProfielApiClient);
			container.bind(BAAS_TYPES.BaasMeetpuntApiClient).to(BarMeetpuntApiClient);
			container.bind(BAAS_TYPES.BaasCommonApiClient).to(BarCommonApiClient);
			break;
		case BaasBackendType.Baas:
			container.bind(BAAS_TYPES.BaasAuditApiClient).to(BaasAuditDefaultApiClient);
			container.bind(BAAS_TYPES.BaasLoggingApiClient).to(BaasLoggingDefaultApiClient);
			container.bind(BAAS_TYPES.BaasMeldingApiClient).to(BaasMeldingDefaultApiClient);
			container.bind(BAAS_TYPES.BaasSecurityApiClient).to(BaasSecurityDefaultApiClient);
			container.bind(BAAS_TYPES.BaasConfigurationApiClient).to(BaasConfigurationDefaultApiClient);
			container.bind(BAAS_TYPES.BaasMeetpuntApiClient).to(BaasMeetpuntDefaultApiClient);
			container.bind(BAAS_TYPES.BaasCommonApiClient).to(BaasCommonDefaultApiClient);
	}
}
function defineAxios(container) {
	const baasInstance = axios.create();
	const baasSilentInstance = axios.create();
	const plainInstance = axios.create();
	container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(baasInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES.Baas);
	container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(baasSilentInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES._BaasSilent);
	container.bind(BAAS_TYPES.AxiosInstance).toConstantValue(plainInstance).whenNamed(BAAS_AXIOS_INSTANCE_TYPES.Plain);
	const interceptor = container.get(BaasInterceptor);
	baasInstance.interceptors.request.use((cfg) => interceptor.onRequest(cfg));
	baasInstance.interceptors.response.use((resp) => interceptor.onResponse(resp), (err) => interceptor.onError(err));
	const silentInterceptor = container.get(BaasInterceptor);
	silentInterceptor.silent = true;
	baasSilentInstance.interceptors.request.use((cfg) => silentInterceptor.onRequest(cfg));
	baasSilentInstance.interceptors.response.use((resp) => silentInterceptor.onResponse(resp), (err) => silentInterceptor.onError(err));
}
function defineI18n(container, options) {
	const i18n = new BaasI18n(options);
	container.bind(BaasI18n).toConstantValue(i18n);
}
function defineHubStuff(container) {
	container.bind(BarHubClient).toSelf().inSingletonScope();
	container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarClearStateHubMessageHandler).inSingletonScope();
	container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarCustomHubMessageHandler).inSingletonScope();
	container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarLogOffHubMessageHandler).inSingletonScope();
	container.bind(_BAAS_TYPES.BarHubMessageHandler).to(BarPushNotificationHubMessageHandler).inSingletonScope();
}
async function bootstrapBaasApp(options = {}, iocSetup = void 0) {
	if (!options) options = {};
	setDefaults(options);
	const container = bootstrapBaasIoc(options);
	if (iocSetup) iocSetup(container);
	const outcome = await container.get(BaasBootstrap).bootstrap();
	return {
		success: outcome === BaasBootstrapOutcome.Initialized,
		outcome,
		appInstance: { container }
	};
}
function setDefaults(options) {
	if (options.localhost == null) options.localhost = checkLocalhost();
	if (!options.connectivity) options.connectivity = BaasConnectivity.Online;
	if (!options.backendType) options.backendType = BaasBackendType.Baas;
	if (!options.auth) options.auth = new BaasAuthOptions();
	options.auth._originalContextTransportModus = options.auth.contextTransportModus;
	if (!options.auth.contextTransportModus) options.auth.contextTransportModus = options.localhost ? BaasContextTransportModus.Header : BaasContextTransportModus.Cookie;
	if (options.auth.syncBasiscontextAfterRequestCompletion === void 0) options.auth.syncBasiscontextAfterRequestCompletion = true;
	if (!options.cacheStorage) options.cacheStorage = {};
	if (!options.cacheStorage.type) options.cacheStorage.type = "idb";
}
var BarGvkSettings = class {
	constructor() {
		this.storageMode = BarGvkStorageMode.Default;
		this.compatibilityMode = BarGvkCompatibilityMode.Version_2;
	}
};
var BaasActieFromContext = class {
	constructor(actie) {
		this.includeVoorwie = true;
		this.includeHoedanigheid = true;
		if (actie != null) {
			this.actie = actie.actie;
			this.includeVoorwie = actie.includeVoorwie === void 0 ? true : actie.includeVoorwie;
			this.includeHoedanigheid = actie.includeHoedanigheid === void 0 ? true : actie.includeHoedanigheid;
		}
	}
};
export { BAAS_AXIOS_INSTANCE_TYPES, BAAS_CUSTOM_EVENT, BAAS_METADATA_KEY, BAAS_TYPES, BaasActie, BaasActieFromContext, BaasAppState, BaasAuditService, BaasAuditType, BaasAuthOptions, BaasAuthService, BaasBackendType, BaasBootstrap, BaasBootstrapOptions, BaasBootstrapOutcome, BaasBrowserCapabilities, BaasConfigurationService, BaasConnectivity, BaasContextService, BaasContextTransportModus, BaasI18n, BaasLogLevel, BaasLoggingService, BaasMeetpuntService, BaasMeldingService, BaasOidcAuthService, BaasSecurityLevel, BaasSecurityService, BaasSession, BaasToepassingService, BaasTransportHelper, BarAuthValues, BarCacheManager, BarCacheStore, BarCacheStoreType, BarGvkCompatibilityMode, BarGvkManager, BarGvkSettings, BarGvkStorageMode, BarHubClient, BarIdentity, BarIdentityType, BarIdpClient, BarIdpClientOptions, BarKlant, BarNotificationManager, BarStopwatch, BarStore, DB_NAME_APP, DB_NAME_APP_USER, DB_NAME_GLOBAL, DB_NAME_GVK, DB_NAME_USER, LOCALE, _BAAS_SHARED_CONSTANTS, _BaasApm, _BaasAuthFailedOrchestrator, _BaasContextManager, _BaasProfielService, _BaasStaticConfigurationApiClient, _BaasTransportHelper, _BarAppInfoManager, _OopsDomService, _OopsType, _addAuditInfoOnDocument, _checkCompatibleOrigin, _getDefaultCacheEntryOptions, asciiEncode, baasContext, baasI18n, bootstrapBaasApp, checkDateObject, checkHasParentDomElement, checkLocalhost, checkSsl, checkUrlAbsolute, clearQueryStringParams, concatUrl, convertToISOString, deepClone, deleteAllCookies, deleteAllIndexedDb, deleteCookie, encodeHeaders, ensureGlobal, extractUrlOrigin, extractUrlParts, generateBaasAuthHeaders, generateRandomNumber, generateUuid, getParamNames, getStack, getStorageAccess, openUrl, parseQueryStringValue, readCookie, resolveUrlFromOrigin, resolveValue, versionCompare };

//# sourceMappingURL=lvflanders-bar.mjs.map